import { defineAsyncComponent } from "vue";
import {
  CREATE_ENTITY_APPLICATION_ONLY,
  DELETE_APPLICATION_URL,
  DOWNLOAD_PENDING_APPLICATIONS_DOCUMENTS_ZIP,
  ENTITY_REGISTER,
  GET_ENTITY_TYPE_FORMS,
  get_headers,
  get_project_id,
  get_token,
  LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
  LIST_SUB_ENTITIES_BY_PARENT_APPLICATION,
  VIEW_ENTITY_TYPE_URL,
  VIEW_PENDING_APPLICATIONS_URL,
  LIST_JOB_ADVERTS_URL,
  MOVE_ENTITY_URL,
  GET_COMPLIANCE_THRESHOLDS,
  SUBMIT_APPLICATIONS_TO_ORCAA_URL,
  SUBMITTED,
} from "@/utils/constants/constants.js";
import {
  build_pending_documents_zip_params,
  download_pending_documents_zip,
  resolve_pending_zip_error_message,
} from "@/views/probaworkflows_base/management/trust_applications/pending_applications/pending_documents_zip_helpers.js";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import { validate_email, validate_non_sub_zero_number, validate_phone_number } from "@/utils/validations/validate_inputs";
import axios from "axios";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import $ from 'jquery';
import {
  APPLICANT_COMPLIANCE_SCORE_KEYS,
  APPLICANT_COMPLIANCE_THRESHOLDS,
} from "@/utils/config/compliance_card_thresholds";

function add_profile_option(options, id, name) {
  if (Number.isFinite(id) && id > 0 && name) {
    options.set(String(id), name);
  }
}
function add_current_profile_option(options, entity_type_id, entity_type_name) {
  if (entity_type_id && entity_type_name) {
    options.set(String(entity_type_id), entity_type_name);
  }
}
function get_application_profile_id(application) {
  return Number(application?.entity_type?.id || application?.type_id || application?.entity_type_id);
}
function get_application_profile_name(application) {
  return application?.entity_type?.entity_type_name || application?.entity_type_name || application?.entity_type?.name;
}
function add_application_profile_option(options, application) {
  add_profile_option(options, get_application_profile_id(application), get_application_profile_name(application));
}

function build_threshold_ranges(thresholds) {
  return [
    {
      label: "Qualified",
      start: thresholds.qualifiedStartPercent,
      end: thresholds.qualifiedEndPercent,
    },
    {
      label: "Partially Qualified",
      start: thresholds.partiallyQualifiedStartPercent,
      end: thresholds.partiallyQualifiedEndPercent,
    },
    {
      label: "Unqualified",
      start: thresholds.unqualifiedStartPercent,
      end: thresholds.unqualifiedEndPercent,
    },
  ];
}

function normalize_positive_number(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}
function get_advert_option_entity_type_id(advert) {
  const candidates = [advert?.entity_type_id, advert?.type_id, advert?.job_profile_id, advert?.entity_type?.id, advert?.job_profile?.id];
  return Number(candidates.find((value) => value) || 0);
}
function get_advert_option_title(advert) {
  const candidates = [advert?.advert_title, advert?.title, advert?.job_title, advert?.entity_type_name, advert?.entity_type?.entity_type_name];
  return candidates.find((value) => value);
}
function get_advert_option_reference(advert) {
  return advert?.reference_number || advert?.reference || advert?.advert_reference_number || '';
}
function matches_entity_type_filter(candidate_id, filter_id) {
  return !filter_id || !candidate_id || candidate_id === Number(filter_id);
}
function add_job_advert_option(options, advert, filter_id) {
  const id = Number(advert?.id || advert?.advert_id);
  if (!matches_entity_type_filter(get_advert_option_entity_type_id(advert), filter_id)) return;
  const option = build_job_advert_option(id, advert);
  if (option) options.set(option.id, option);
}
function add_application_advert_option(options, application, filter_id) {
  if (!matches_entity_type_filter(get_application_profile_id(application), filter_id)) return;
  const id = Number(application?.advert_id || application?.advert?.id);
  const option = build_application_advert_option(id, application);
  if (option) options.set(option.id, option);
}
function build_job_advert_option(id, advert) {
  const title = get_advert_option_title(advert);
  const reference_number = get_advert_option_reference(advert);
  if (!has_valid_advert_option(id, title || reference_number)) return null;
  return { id: String(id), title: title || reference_number, reference_number };
}
function build_application_advert_option(id, application) {
  const title = get_application_advert_option_title(application);
  if (!has_valid_advert_option(id, title)) return null;
  const option = { id: String(id), title, reference_number: get_application_advert_option_reference(application) };
  return option;
}
function get_application_advert_option_title(application) {
  const advert = application?.advert || {};
  const candidates = [application?.advert_title, advert.title, advert.reference_number];
  return candidates.find((value) => value);
}
function get_application_advert_option_reference(application) {
  const advert = application?.advert || {};
  return advert.reference_number || application?.advert_reference_number || '';
}
function has_valid_advert_option(id, label) {
  return Number.isFinite(id) && id > 0 && !!label;
}

const GlobalTable = defineAsyncComponent(() =>
  import("@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue")
);

export default {
  name: "PendingApplications",
  mixins: [alert_mixin, formatting_mixins],
  components: { GlobalTable },
  data: () => ({
    token: "",
    project_id: "",
    selected_status: SUBMITTED,
    display_name: "",
    entity_type_name:"",
    entity_type_id: "",
    advert_id: "",
    is_downloading_documents_zip: false,
    is_downloading_selection_grid_excel: false,
    document_export_progress: 0,
    document_export_label: "",
    document_export_detail: "",
    document_export_timer: null,
    is_submitting_orcaa_on_load: false,
    auto_orcaa_attempted_key: "",
    headers: {},
    pending_applications_list: [],
    job_adverts_list: [],
    table_headers: [],
    sanitized_pending_applications_list: [],
    displayed_applications_list: [],
    total_entities: 0,
    user_ids: [],
    profile_role_ids: [],
    application_row_map: {},
    show_assign_modal: false,
    current_form: {
      parent_id: null,
      parent_name: '',
      child_id: '',
      parent_application_id: '',
      parent_entity_profile_id: '',
    },
    available_children: [],
    displayed_assigned_children: [],
    entity_type: [],
    form_list: [],
    selected_form_id: "",
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    assign_sub_entity_type_id: '',
    assign_sub_entity_options: [],
    assign_form_list: [],
    assign_selected_form_id: '',
    assign_first_name: '',
    assign_last_name: '',
    assign_email: '',
    assign_phone_number: '',
    filters: {
      search: "",
      entity_type_id: "",
      advert_id: "",
      date_from: "",
      date_to: "",
    },
    show_filters: false,
    assign_entity_name: "",
    lock_entity_type: false,
    // Track all available statuses from backend
    available_statuses: [],
    include_email: false,
    show_move_modal: false,
    move_form: {
      entity_profile_id: null,
      new_entity_type_id: "",
      form_ids: [],
      selected_form_id: "",
    },
    move_entity_type_options: [], // reuse this.entity_type if you want
    move_form_list: [],
    active_compliance_card: "all",
    table_render_key: 0,
    compliance_thresholds: APPLICANT_COMPLIANCE_THRESHOLDS,
    compliance_thresholds_by_entity_type: {},
    active_route_load_signature: "",
    completed_route_load_signature: "",
    pending_route_load_promise: null,
  }),
  created() {
    this.token = get_token();
    this.project_id = get_project_id();
    this.headers = get_headers();

    if (!validate_non_sub_zero_number(parseInt(this.project_id, 10))) {
      this.show_error("Project ID must not be null or zero");
    }
  },

  computed: {
  totalApplicants() {
    return this.displayed_applications_list.length;
  },

  qualifiedApplicants() {
    return this.pending_applications_list.filter(app => {
      return this.get_threshold_assessment(app) === "Qualified";
    }).length;
  },

  partiallyQualifiedApplicants() {
    return this.pending_applications_list.filter(app => {
      return this.get_threshold_assessment(app) === "Partially Qualified";
    }).length;
  },

  unqualifiedApplicants() {
    return this.pending_applications_list.filter(app => {
      return this.get_threshold_assessment(app) === "Unqualified";
    }).length;
  },

  qualifiedThresholdLabel() {
    return this.get_active_threshold_label("qualified");
  },

  partiallyQualifiedThresholdLabel() {
    return this.get_active_threshold_label("partially_qualified");
  },

  unqualifiedThresholdLabel() {
    return this.get_active_threshold_label("unqualified");
  },
  applicant_profile_options() {
    const options = new Map();
    (this.entity_type || []).forEach((profile) => {
      const id = Number(profile?.id);
      const name = profile?.name || profile?.entity_type_name;
      add_profile_option(options, id, name);
    });
    (this.pending_applications_list || []).forEach((application) => {
      add_application_profile_option(options, application);
    });
    add_current_profile_option(options, this.entity_type_id, this.entity_type_name);
    return Array.from(options.entries()).map(([id, name]) => ({ id, name }));
  },
  get_selected_applicant_advert_option() {
    return this.applicant_advert_options.find(
      option => String(option.id) === String(this.filters.advert_id)
    ) || null;
  },
  get_selected_applicant_advert_title() {
    if (!this.filters.advert_id) return "";
    return this.get_selected_applicant_advert_option?.title || "";
  },
  get_selected_applicant_profile_label() {
    const selected = this.applicant_profile_options.find(
      option => String(option.id) === String(this.filters.entity_type_id)
    );
    return selected?.name || this.entity_type_name || "All Job Profiles";
  },

  selected_applicant_profile_name() {
    const selected_advert_title = this.get_selected_applicant_advert_title;
    if (selected_advert_title) return selected_advert_title;
    if (!this.filters.entity_type_id) return "All Job Profiles";
    return this.get_selected_applicant_profile_label;
  },

  applicant_advert_options() {
    const options = new Map();
    (this.job_adverts_list || []).forEach((advert) => {
      add_job_advert_option(options, advert, this.filters.entity_type_id);
    });

    (this.pending_applications_list || []).forEach((application) => {
      add_application_advert_option(options, application, this.filters.entity_type_id);
    });
    return Array.from(options.values());
  }
},

  mounted() {
    this.entity_type_name = sessionStorage.getItem('entity_type_name')
    this.filter_by = sessionStorage.getItem("filter_by")
      ? JSON.parse(sessionStorage.getItem("filter_by"))
      : [];

    this.on_route_change();
    this.load_entity_type_context();
  },
  beforeUnmount() {
    this.clear_document_export_timer();
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        this.on_route_change();
      }
    },

    entity_type_id(newVal) {
      if (newVal) this.get_forms();
    },

    "filters.entity_type_id"(newVal) {
      if (!newVal) {
        this.filters.advert_id = "";
      }
    },

    show_assign_modal(val) {
      if (typeof document !== 'undefined') {
        document.body.classList.toggle('v-modal-open', !!val);
      }
    }
  },
  methods: {
    get_application_status_name(application) {
      return application?.status_name || application?.status?.status || "";
    },
    get_application_entity_type_name(application) {
      return (
        application?.entity_type_name ||
        application?.entity_type?.entity_type_name ||
        application?.entity_type?.name ||
        ""
      );
    },
    get_application_user_name(application) {
      return application?.applicant_name || application?.entity_name || "";
    },
    /** Download all applicant documents as a zip using the current route filters. */
    async download_all_documents_zip(merge_documents = false) {
      const params = this.build_documents_zip_params(merge_documents);
      if (!this.can_download_documents_zip(params)) return;
      this.is_downloading_documents_zip = true;
      this.start_document_export_progress(merge_documents);
      try {
        await download_pending_documents_zip(
          axios,
          DOWNLOAD_PENDING_APPLICATIONS_DOCUMENTS_ZIP,
          this.headers,
          params,
          this.update_document_export_download_progress,
        );
        this.complete_document_export_progress();
      } catch (error) {
        this.show_error(await this.resolve_documents_zip_error(error));
        this.reset_document_export_progress();
      } finally {
        this.is_downloading_documents_zip = false;
      }
    },
    /** Start an estimated export progress indicator for long document downloads. */
    start_document_export_progress(merge_documents) {
      this.clear_document_export_timer();
      this.document_export_progress = 4;
      this.document_export_label = merge_documents ? "Preparing merged PDF" : "Preparing applicant folders ZIP";
      this.document_export_detail = this.build_document_export_detail(merge_documents);
      this.document_export_timer = window.setInterval(
        this.advance_document_export_progress,
        700,
      );
    },
    /** Return progress helper text for the selected export action. */
    build_document_export_detail(merge_documents) {
      const count = this.pending_applications_list.length;
      const noun = count === 1 ? "applicant" : "applicants";
      const action = merge_documents ? "merging PDF documents" : "packaging folders";
      return `${action} for ${count} ${noun}`;
    },
    /** Advance estimated progress while the server prepares the export. */
    advance_document_export_progress() {
      if (this.document_export_progress >= 92) return;
      const increment = this.document_export_progress < 55 ? 6 : 3;
      this.document_export_progress = Math.min(92, this.document_export_progress + increment);
    },
    /** Use browser download progress when a content-length is available. */
    update_document_export_download_progress(event) {
      if (!event?.total) return;
      const loaded_percent = Math.round((event.loaded / event.total) * 100);
      const progress = Math.max(this.document_export_progress, loaded_percent);
      this.document_export_progress = Math.min(98, progress);
    },
    /** Complete and then hide the export progress indicator. */
    complete_document_export_progress() {
      this.clear_document_export_timer();
      this.document_export_progress = 100;
      window.setTimeout(this.reset_document_export_progress, 600);
    },
    /** Reset export progress state after completion or failure. */
    reset_document_export_progress() {
      this.clear_document_export_timer();
      this.document_export_progress = 0;
      this.document_export_label = "";
      this.document_export_detail = "";
    },
    /** Clear the export progress timer if it is active. */
    clear_document_export_timer() {
      if (!this.document_export_timer) return;
      window.clearInterval(this.document_export_timer);
      this.document_export_timer = null;
    },
    build_documents_zip_params(merge_documents = false) {
      const route_query = this.$route?.query || {};
      const download_query = { ...route_query, status: route_query.status || SUBMITTED, ...(merge_documents ? { merge_documents: true } : {}) };
      return build_pending_documents_zip_params(this.project_id, this.entity_type_id, download_query);
    },
    can_download_documents_zip(params) {
      if (!this.pending_applications_list.length) {
        this.show_error("There are no applicants to download.");
        return false;
      }
      if (params.project_id) return true;
      this.show_error("Project context is missing.");
      return false;
    },
    async resolve_documents_zip_error(error) {
      const response_data = error.response?.data;
      const api_message = response_data ? await resolve_pending_zip_error_message(response_data) : "";
      return api_message || error.message || "Failed to download applicant documents.";
    },
    /** Export the current selection grid fields to an Excel-readable file. */
    download_selection_grid_excel() {
      if (!this.pending_applications_list.length) {
        this.show_error("There are no applicants to export.");
        return;
      }

      const export_headers = this.table_headers || [];
      if (!export_headers.length) {
        this.show_error("There are no selection grid fields to export.");
        return;
      }

      this.is_downloading_selection_grid_excel = true;
      try {
        const export_rows = (this.sanitized_pending_applications_list || []).map((application) => (
          export_headers.map((header) => this.format_selection_grid_excel_value(
            this.get_selection_grid_export_value(application, header)
          ))
        ));
        const workbook_xml = this.build_selection_grid_workbook_xml(export_headers, export_rows);
        const blob = new Blob([workbook_xml], {
          type: "application/vnd.ms-excel;charset=utf-8;",
        });
        const download_url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const date_stamp = new Date().toISOString().slice(0, 10);
        const file_name = this.slugify_selection_grid_file_name(
          `selection-grid-${this.entity_type_name || "applicants"}-${date_stamp}.xls`
        );

        link.href = download_url;
        link.download = file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(download_url);
      } catch (error) {
        this.show_error(error.message || "Failed to export the selection grid.");
      } finally {
        this.is_downloading_selection_grid_excel = false;
      }
    },

    get_selection_grid_export_value(application, header) {
      const matching_entry = Object.entries(application || {}).find(([key]) => key === header);
      return matching_entry ? matching_entry[1] : undefined;
    },

    format_selection_grid_excel_value(value) {
      if (Array.isArray(value)) {
        return value.join("\n");
      }
      if (value === null || typeof value === "undefined" || value === "") {
        return "-";
      }
      return value.toString();
    },

    escape_selection_grid_excel_xml(value) {
      return this.format_selection_grid_excel_value(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    },

    build_selection_grid_workbook_xml(headers, rows) {
      const xml_headers = headers.map((header) => (
        `<Cell ss:StyleID="Header"><Data ss:Type="String">${this.escape_selection_grid_excel_xml(header)}</Data></Cell>`
      )).join("");
      const xml_rows = rows.map((row) => (
        `<Row>${row.map((value) => (
          `<Cell ss:StyleID="Wrap"><Data ss:Type="String">${this.escape_selection_grid_excel_xml(value)}</Data></Cell>`
        )).join("")}</Row>`
      )).join("");

      return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:html="http://www.w3.org/TR/REC-html40">
  <Styles>
    <Style ss:ID="Default" ss:Name="Normal">
      <Alignment ss:Vertical="Top"/>
      <Font ss:FontName="Calibri" x:Family="Swiss" ss:Size="11" ss:Color="#000000"/>
      <Interior/>
      <NumberFormat/>
      <Protection/>
    </Style>
    <Style ss:ID="Header" ss:Parent="Default">
      <Font ss:FontName="Calibri" x:Family="Swiss" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
      <Interior ss:Color="#00662E" ss:Pattern="Solid"/>
      <Alignment ss:WrapText="1" ss:Vertical="Top"/>
    </Style>
    <Style ss:ID="Wrap" ss:Parent="Default">
      <Alignment ss:WrapText="1" ss:Vertical="Top"/>
    </Style>
  </Styles>
  <Worksheet ss:Name="Selection Grid">
    <Table>
      <Row>${xml_headers}</Row>
      ${xml_rows}
    </Table>
  </Worksheet>
</Workbook>`;
    },

    slugify_selection_grid_file_name(file_name) {
      return file_name
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .toLowerCase();
    },

    get_threshold_assessment(application) {
      const orcaa_recommendation = this.format_selection_grid_value(
        this.get_orcaa_score_result(application)?.fit_recommendation
      );
      if (orcaa_recommendation) {
        return orcaa_recommendation;
      }

      const assessment = application?.threshold_assessment || application?.compliance_summary?.threshold_assessment;
      if (assessment) {
        return assessment;
      }

      const score_based_assessment = this.get_threshold_assessment_from_score(application);
      if (score_based_assessment) {
        return score_based_assessment;
      }

      const bucket = application?.compliance_bucket;
      if (bucket === "qualified") {
        return "Qualified";
      }
      if (bucket === "partially_qualified") {
        return "Partially Qualified";
      }
      return "Unqualified";
    },
    get_threshold_assessment_from_score(application) {
      const score = this.get_compliance_score(application);
      if (score === null) return null;

      const entity_type_id = this.get_application_entity_type_id(application);
      const thresholds = this.get_thresholds_for_entity_type(entity_type_id);
      const range = build_threshold_ranges(thresholds)
        .find((candidate) => score >= candidate.start && score <= candidate.end);
      return range?.label || null;
    },

    normalize_threshold_assessment(assessment) {
      return (assessment || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
    },

    parse_percent_value(value) {
      if (value === null || typeof value === "undefined") {
        return null;
      }

      if (typeof value === "number" && Number.isFinite(value)) {
        return Math.max(0, Math.min(100, value));
      }

      if (typeof value === "string") {
        const cleaned = value.replace(/%/g, "").trim();
        const parsed = Number.parseFloat(cleaned);
        if (Number.isFinite(parsed)) {
          return Math.max(0, Math.min(100, parsed));
        }
      }

      return null;
    },

    get_compliance_score(application) {
      const score_sources = [
        application || {},
        application?.status || {},
        application?.compliance || {},
        application?.compliance_summary || {},
      ];

      for (const source of score_sources) {
        for (const key of APPLICANT_COMPLIANCE_SCORE_KEYS) {
          const value = Reflect.get(source || {}, key);
          const parsed = this.parse_percent_value(value);
          if (parsed !== null) {
            return parsed;
          }
        }
      }

      return null;
    },

    get_application_entity_type_id(application) {
      const raw_value =
        application?.entity_type_id ??
        application?.entity_type?.id ??
        application?.type_id ??
        null;
      const parsed = Number(raw_value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },

    get_thresholds_for_entity_type(entity_type_id) {
      if (!entity_type_id) {
        return this.compliance_thresholds;
      }

      return this.compliance_thresholds_by_entity_type[entity_type_id] || this.compliance_thresholds;
    },

    get_primary_threshold_entity_type_id() {
      const explicit_id = Number(this.filters.entity_type_id || this.entity_type_id || 0);
      if (Number.isFinite(explicit_id) && explicit_id > 0) {
        return explicit_id;
      }

      const unique_entity_type_ids = Array.from(
        new Set(
          (this.pending_applications_list || [])
            .map(application => this.get_application_entity_type_id(application))
            .filter(Boolean)
        )
      );

      return unique_entity_type_ids.length === 1 ? unique_entity_type_ids[0] : null;
    },

    get_active_threshold_label(bucket_name) {
      const entity_type_id = this.get_primary_threshold_entity_type_id();
      if (!entity_type_id) {
        return "Per type config";
      }

      const thresholds = this.get_thresholds_for_entity_type(entity_type_id);
      if (bucket_name === "qualified") {
        return `${thresholds.qualifiedStartPercent}-${thresholds.qualifiedEndPercent}%`;
      }
      if (bucket_name === "partially_qualified") {
        return `${thresholds.partiallyQualifiedStartPercent}-${thresholds.partiallyQualifiedEndPercent}%`;
      }
      return `${thresholds.unqualifiedStartPercent}-${thresholds.unqualifiedEndPercent}%`;
    },

    get_compliance_bucket(application) {
      const score = this.get_compliance_score(application);
      const entity_type_id = this.get_application_entity_type_id(application);
      const thresholds = this.get_thresholds_for_entity_type(entity_type_id);
      const qualified_start = thresholds.qualifiedStartPercent;
      const qualified_end = thresholds.qualifiedEndPercent;
      const partially_start = thresholds.partiallyQualifiedStartPercent;
      const partially_end = thresholds.partiallyQualifiedEndPercent;
      const unqualified_start = thresholds.unqualifiedStartPercent;
      const unqualified_end = thresholds.unqualifiedEndPercent;

      if (score !== null) {
        if (score >= qualified_start && score <= qualified_end) {
          return "qualified";
        }
        if (score >= partially_start && score <= partially_end) {
          return "partially_qualified";
        }
        if (score >= unqualified_start && score <= unqualified_end) {
          return "unqualified";
        }
      }

      const status = (application?.status?.status || "").toLowerCase();
      if (status.includes("unqualified")) {
        return "unqualified";
      }
      if (status.includes("partially")) {
        return "partially_qualified";
      }
      if (status.includes("qualified")) {
        return "qualified";
      }

      return "unqualified";
    },

    set_compliance_card_filter(filter_name) {
      this.active_compliance_card = this.active_compliance_card === filter_name ? "all" : filter_name;
      this.apply_application_filters();
    },

    apply_application_filters() {
      let filtered = Array.from(this.sanitized_pending_applications_list || []);

      const query = (this.filters.search || "").toLowerCase();
      if (query) {
        filtered = filtered.filter(app => {
          const app_id = Number.parseInt(Reflect.get(app || {}, "Application No"), 10);
          const original = this.get_application_by_id(app_id);
          const trust_number = original?.trust_number || '';
          const name_text = (
            Reflect.get(app || {}, "Entity Name") ||
            Reflect.get(app || {}, "Full Name") ||
            ""
          ).toLowerCase();

          return (
            name_text.includes(query) ||
            (Reflect.get(app || {}, "Job Applied For") || Reflect.get(app || {}, "Entity Type") || "").toLowerCase().includes(query) ||
            (Reflect.get(app || {}, "Advert") || "").toLowerCase().includes(query) ||
            String(Reflect.get(app || {}, "Application No") || "").includes(query) ||
            trust_number.toLowerCase().includes(query)
          );
        });
      }

      if (this.filters.entity_type_id) {
        const entity_type_id = Number(this.filters.entity_type_id);

        filtered = filtered.filter(app => {
          const raw_app_no = Reflect.get(app || {}, "Application No");
          const app_id = Number.parseInt(raw_app_no, 10);
          const original = this.get_application_by_id(app_id);

          if (!original) {
            return false;
          }

          return this.get_application_entity_type_id(original) === entity_type_id;
        });
      }

      if (this.filters.advert_id) {
        const advert_id = Number(this.filters.advert_id);

        filtered = filtered.filter(app => {
          const raw_app_no = Reflect.get(app || {}, "Application No");
          const app_id = Number.parseInt(raw_app_no, 10);
          const original = this.get_application_by_id(app_id);

          if (!original) {
            return false;
          }

          const original_advert_id = this.get_application_advert_id(original);
          return original_advert_id === advert_id;
        });
      }

      if (this.filters.date_from) {
        const date_from = new Date(this.filters.date_from);
        filtered = filtered.filter(app => new Date(Reflect.get(app || {}, "Date Created")) >= date_from);
      }

      if (this.filters.date_to) {
        const date_to = new Date(this.filters.date_to);
        date_to.setHours(23, 59, 59, 999);
        filtered = filtered.filter(app => new Date(Reflect.get(app || {}, "Date Created")) <= date_to);
      }

      if (this.active_compliance_card !== "all") {
        filtered = filtered.filter(app => this.matches_active_compliance_card(app));
      }

      filtered = filtered.map((app, index) => this.build_display_application_row(app, index));

      this.displayed_applications_list = filtered;
      // DataTables mutates table DOM; bumping the key forces a clean remount and avoids patch errors.
      this.table_render_key += 1;
    },
    build_display_application_row(app, index) {
      this.ensure_trust_number_cell(app);
      const display_row = this.hide_application_no_column(app);
      Reflect.set(display_row, "No", index + 1);
      return display_row;
    },
    ensure_trust_number_cell(app) {
      if (!this.should_fill_trust_number_cell(app)) return;
      const original = this.get_application_by_id(Reflect.get(app || {}, "Application No"));
      const trust_number = this.get_registered_trust_number(original);
      Reflect.set(app, "Trust Number", trust_number);
    },
    should_fill_trust_number_cell(app) {
      return this.table_headers.includes("Trust Number") && Reflect.get(app || {}, "Trust Number") === undefined;
    },
    get_registered_trust_number(original) {
      if (!this.is_master_registered_application(original)) return "";
      return original?.trust_number || "";
    },
    is_master_registered_application(original) {
      const raw_status = original?.status?.status || "";
      const normalized_status = raw_status.toLowerCase();
      return normalized_status.includes('master registered') || normalized_status.includes('master_registered');
    },
    matches_active_compliance_card(app) {
      const original = this.get_original_application_for_grid_row(app);
      if (!original) return false;
      return this.normalize_threshold_assessment(this.get_threshold_assessment(original)) === this.active_compliance_card;
    },
    get_original_application_for_grid_row(app) {
      const app_id = Number.parseInt(Reflect.get(app || {}, "Application No"), 10);
      return this.get_application_by_id(app_id);
    },

    async on_route_change() {
      this.apply_status_from_route();
      this.apply_entity_type_from_route();
      this.apply_display_name_from_route();

      this.lock_entity_type = false;

      this.compliance_thresholds_by_entity_type = {};

      const route_signature = JSON.stringify({
        path: this.$route?.path || "",
        query: this.$route?.query || {},
        selected_status: this.selected_status || "",
        entity_type_id: this.entity_type_id || "",
        advert_id: this.advert_id || "",
      });

      if (this.active_route_load_signature === route_signature && this.pending_route_load_promise) {
        return this.pending_route_load_promise;
      }

      if (
        this.completed_route_load_signature === route_signature &&
        !this.pending_route_load_promise
      ) {
        return Promise.resolve();
      }

      this.active_route_load_signature = route_signature;
      return this.view_pending_applications(route_signature);
    },

    async load_compliance_thresholds(entity_type_ids = []) {
      const normalized_ids = Array.from(
        new Set(
          (entity_type_ids || [])
            .map(value => Number(value))
            .filter(value => Number.isFinite(value) && value > 0)
        )
      );

      if (!normalized_ids.length) {
        return;
      }

      try {
        const responses = await Promise.all(
          normalized_ids.map(entity_type_id =>
            axios.get(GET_COMPLIANCE_THRESHOLDS, {
              headers: this.headers,
              params: { entity_type_id },
            })
          )
        );

        const next_thresholds = { ...this.compliance_thresholds_by_entity_type };
        normalized_ids.forEach((entity_type_id, index) => {
          const data = responses[index]?.data?.data || {};
          next_thresholds[entity_type_id] = {
            qualifiedStartPercent: Number(
              data.qualified_start_percent ?? APPLICANT_COMPLIANCE_THRESHOLDS.qualifiedStartPercent
            ),
            qualifiedEndPercent: Number(
              data.qualified_end_percent ?? APPLICANT_COMPLIANCE_THRESHOLDS.qualifiedEndPercent
            ),
            partiallyQualifiedStartPercent: Number(
              data.partially_qualified_start_percent ?? APPLICANT_COMPLIANCE_THRESHOLDS.partiallyQualifiedStartPercent
            ),
            partiallyQualifiedEndPercent: Number(
              data.partially_qualified_end_percent ?? APPLICANT_COMPLIANCE_THRESHOLDS.partiallyQualifiedEndPercent
            ),
            unqualifiedStartPercent: Number(
              data.unqualified_start_percent ?? APPLICANT_COMPLIANCE_THRESHOLDS.unqualifiedStartPercent
            ),
            unqualifiedEndPercent: Number(
              data.unqualified_end_percent ?? APPLICANT_COMPLIANCE_THRESHOLDS.unqualifiedEndPercent
            ),
          };
        });

        this.compliance_thresholds_by_entity_type = next_thresholds;

        const primary_entity_type_id = this.get_primary_threshold_entity_type_id() || normalized_ids[0];
        this.compliance_thresholds = this.get_thresholds_for_entity_type(primary_entity_type_id);
      } catch (_) {
        // Keep defaults if settings are not available.
      }
    },

    apply_status_from_route() {
      const route_status = String(this.$route?.query?.status || "").trim();
      this.selected_status = route_status || SUBMITTED;
    },

    apply_display_name_from_route() {
      const cached_display_name = sessionStorage.getItem('display_name') || '';
      if (cached_display_name) {
        this.display_name = cached_display_name;
        return;
      }

      this.display_name = '';
    },

    apply_entity_type_from_route() {
      const raw = (this.$route.query.entity_type_id ?? '').toString().trim();
      this.entity_type_id = /^\d+$/.test(raw) ? Number(raw) : '';

      const advert_raw = (this.$route.query.advert_id ?? '').toString().trim();
      this.advert_id = /^\d+$/.test(advert_raw) ? Number(advert_raw) : '';

      this.filters = {
        ...this.filters,
        entity_type_id: this.entity_type_id || '',
        advert_id: this.advert_id || '',
      };
    },

    async view_pending_applications(route_signature = "") {
      const load_promise = this.show_loader("Loading pending applications...")
        .then(loader => this.load_pending_applications_with_loader(loader));

      this.pending_route_load_promise = load_promise.finally(() => {
        this.finalize_pending_route_load(route_signature);
      });

      return this.pending_route_load_promise;
    },
    async load_pending_applications_with_loader(loader) {
      this.reset_pending_applications_table();
      const params = this.build_pending_applications_params();
      if (!params) return this.close_loader(loader);
      return axios.get(this.get_api_url(), { headers: this.headers, params })
        .then(response => this.handle_pending_applications_response(response, loader))
        .catch(error => this.handle_pending_applications_error(error, loader));
    },
    reset_pending_applications_table() {
      this.displayed_applications_list = [];
      this.table_headers = [];
    },
    build_pending_applications_params() {
      const project_id = parseInt(this.project_id, 10);
      if (!validate_non_sub_zero_number(project_id)) {
        this.show_error("Project ID must not be null or zero");
        return null;
      }
      const status_id = this.get_numeric_status_id();
      const params = {
        project_id,
        submitted_only: true,
        include_compliance: false,
        compact: true,
      };
      this.add_optional_pending_application_params(params, status_id);
      return params;
    },
    add_optional_pending_application_params(params, status_id) {
      this.get_optional_pending_application_params(status_id).forEach(({ key, value }) => {
        this.set_optional_param(params, key, value);
      });
    },
    get_optional_pending_application_params(status_id) {
      const optional_params = [
        { key: "entity_type_id", value: this.entity_type_id },
        { key: "advert_id", value: this.advert_id },
        { key: "status_id", value: status_id },
      ];
      return optional_params;
    },
    set_optional_param(params, key, value) {
      if (value) Reflect.set(params, key, value);
    },
    async handle_pending_applications_response(response, loader) {
      const response_data = response.data;
      if (response_data.status !== "success") {
        this.show_error("Error occurred while pulling pending applications");
        return this.close_loader(loader);
      }
      this.apply_pending_applications_response(response_data);
      const close_loader_promise = this.close_loader(loader);
      this.refresh_applicant_job_adverts();
      return close_loader_promise.then(() => this.submit_missing_orcaa_for_loaded_applications());
    },
    apply_pending_applications_response(response_data) {
      this.application_user_map = {};
      const pending_entities = response_data?.data?.list_pending_entities || [];
      this.pending_applications_list = pending_entities.map((entity) => this.map_pending_application_entity(entity));
      this.extract_available_statuses(pending_entities);
      this.form_idsArray = this.pending_applications_list.map((entity) => entity.form?.id).filter(Boolean);
      this.prep_tables();
      this.counts_initialized = true;
    },
    map_pending_application_entity(entity) {
      const user_id = this.get_user_id_from_entity(entity);
      this.application_user_map[entity.id] = user_id;
      const mapped_entity = { ...entity, user_id };
      return mapped_entity;
    },
    refresh_applicant_job_adverts() {
      this.load_applicant_job_adverts(this.entity_type_id).then(() => {
        this.prep_tables();
      }).catch(() => {
        this.prep_tables();
      });
    },
    handle_pending_applications_error(error, loader) {
      const errorMsg = error.response?.data?.message || "An unexpected error occurred";
      this.show_error(`Error retrieving applications: ${errorMsg}`);
      return this.close_loader(loader);
    },
    finalize_pending_route_load(route_signature) {
      if (this.active_route_load_signature === route_signature) {
        this.completed_route_load_signature = route_signature;
        this.active_route_load_signature = "";
      }
      this.pending_route_load_promise = null;
    },

    async submit_missing_orcaa_for_loaded_applications() {
      const route_key = this.build_auto_orcaa_route_key();
      const application_ids = this.get_auto_orcaa_application_ids(route_key);
      if (!application_ids.length) return;
      this.auto_orcaa_attempted_key = route_key;
      this.is_submitting_orcaa_on_load = true;
      try {
        await axios.post(
          SUBMIT_APPLICATIONS_TO_ORCAA_URL,
          { application_ids },
          { headers: this.headers }
        );
        await this.refresh_pending_applications_silently();
      } catch (error) {
        const message = error.response?.data?.message || "Some applicants could not be submitted to Orcaa.";
        this.show_error(message);
      } finally {
        this.is_submitting_orcaa_on_load = false;
      }
    },
    /** Refresh applicants after background ORCAA scoring without showing the page loader. */
    async refresh_pending_applications_silently() {
      const params = this.build_pending_applications_params();
      if (!params) return;
      const response = await axios.get(this.get_api_url(), {
        headers: this.headers,
        params,
      });
      this.apply_silent_pending_applications_response(response);
    },
    /** Apply a silent applicants refresh only when the API returns valid data. */
    apply_silent_pending_applications_response(response) {
      const response_data = response?.data;
      if (response_data?.status !== "success") return;
      this.apply_pending_applications_response(response_data);
      this.refresh_applicant_job_adverts();
    },
    build_auto_orcaa_route_key() {
      return [
        this.entity_type_id || "all",
        this.advert_id || "all",
        this.pending_applications_list.map(application => application.id).join(","),
      ].join(":");
    },
    get_auto_orcaa_application_ids(route_key) {
      if (this.is_submitting_orcaa_on_load || !this.pending_applications_list.length) return [];
      if (this.auto_orcaa_attempted_key === route_key) return [];
      return this.pending_applications_list
        .filter(application => this.should_auto_submit_orcaa(application))
        .map(application => Number(application.id))
        .filter(application_id => Number.isFinite(application_id) && application_id > 0);
    },

    should_auto_submit_orcaa(application) {
      const has_orcaa_score = !!this.get_orcaa_score_result(application);
      const has_skip_identity_validation = this.to_boolean_flag(
        application?.orcaa_skip_identity_validation
      );
      return !has_orcaa_score || !has_skip_identity_validation;
    },

    to_boolean_flag(value) {
      if (typeof value === "boolean") {
        return value;
      }
      if (value === null || typeof value === "undefined") {
        return false;
      }

      const normalized = String(value).trim().toLowerCase();
      return ["true", "1", "yes", "y"].includes(normalized);
    },

    extract_available_statuses(entities) {
      const statusSet = new Set();
      entities.forEach(entity => {
        const status_name = this.get_application_status_name(entity);
        if (status_name) {
          statusSet.add(status_name);
        }
      });
      this.available_statuses = Array.from(statusSet);
    },

    reset_move_modal() {
      this.show_move_modal = false;
      this.move_form = {
        entity_profile_id: null,
        new_entity_type_id: "",
        form_ids: [],
        selected_form_id: "",
      };
      this.move_form_list = [];
    },


    open_move_modal(row) {
      const application_id = Number(Reflect.get(row || {}, "Application No") || 0);
      if (!application_id) {
        this.show_error("Unable to find Application No for this row.");
        return;
      }

      const original = this.pending_applications_list.find(a => Number(a.id) === application_id);
      if (!original) {
        this.show_error("Original application not found.");
        return;
      }

      const entity_profile_id = Number(original.entity || 0);
      if (!entity_profile_id) {
        this.show_error("Unable to find entity_profile_id (entity) on application.");
        return;
      }

      this.move_form.entity_profile_id = entity_profile_id;

      this.move_form.new_entity_type_id = "";
      this.move_form.form_ids = [];
      this.move_form.selected_form_id = "";
      this.move_form_list = [];

      $("#open_move_entity_modal_btn").trigger("click");
    },

    on_move_form_change() {
      const selected = Number(this.move_form.selected_form_id || 0);
      this.move_form.form_ids = selected ? [selected] : [];
    },



    async on_move_entity_type_change() {
      this.move_form_list = [];
      this.move_form.form_ids = [];
      this.move_form.selected_form_id = "";
      const entity_type_id = Number(this.move_form.new_entity_type_id || 0);
      if (!entity_type_id) return;

      try {
        const res = await axios.get(GET_ENTITY_TYPE_FORMS, {
          headers: this.headers,
          params: { entity_type_id },
        });

        // Your API sometimes returns JSON string
        const parsed = typeof res.data === "string" ? JSON.parse(res.data) : res.data;

        // common patterns: parsed.data OR parsed.data.data
        const forms = Array.isArray(parsed?.data)
          ? parsed.data
          : Array.isArray(parsed?.data?.data)
            ? parsed.data.data
            : [];

        this.move_form_list = forms;
      } catch (e) {
        const msg = e.response?.data?.message || e.message || "Failed to load forms.";
        this.show_error(msg);
      }
    },

    async submit_move_entity() {
      return this.show_loader("Moving entity...")
        .then(async (loader) => {
          const entity_profile_id = Number(this.move_form.entity_profile_id || 0);
          if (!entity_profile_id) {
            this.show_error("Missing entity_profile_id.");
            return this.close_loader(loader);
          }

          const new_entity_type_id = Number(this.move_form.new_entity_type_id || 0);
          if (!new_entity_type_id) {
            this.show_error("Please select a new entity type.");
            return this.close_loader(loader);
          }

          const form_ids = Array.isArray(this.move_form.form_ids)
            ? this.move_form.form_ids.map(id => Number(id)).filter(Boolean)
            : [];

          if (form_ids.length === 0) {
            this.show_error("Please select at least one form.");
            return this.close_loader(loader);
          }

          const payload = {
            entity_profile_id,
            new_entity_type_id,
            form_ids,
          };

          try {
            const res = await axios.post(MOVE_ENTITY_URL, payload, { headers: this.headers });

            const body = typeof res.data === "string" ? JSON.parse(res.data) : res.data;

            if (body?.status !== "success") {
              this.show_error(body?.message || "Failed to move entity.");
              return this.close_loader(loader);
            }

            this.show_success(body?.message || "Entity moved successfully.");

            // Close modal (works if you already have close_modal helper)
            // Otherwise bootstrap will close via data-bs-dismiss on cancel only.
            // We'll force close by clicking the close button if needed.
            this.reset_move_modal();
            $("#move_entity_modal .btn-close").trigger("click");

            // Refresh list
            await this.view_pending_applications();

            return this.close_loader(loader);
          } catch (e) {
            const msg = e.response?.data?.message || e.message || "Error moving entity.";
            this.show_error(msg);
            return this.close_loader(loader);
          }
        });
    },


    get_api_url() {
      return VIEW_PENDING_APPLICATIONS_URL;
    },

    get_filter_value() {
      return undefined;
    },

    submitted_status_filter() {
      return SUBMITTED.toLowerCase().replace(/-/g, "_");
    },

    get_numeric_status_id() {
      const status_id = this.$route?.query?.status_id;
      return /^\d+$/.test(String(status_id || "")) ? Number(status_id) : null;
    },

      get_user_id_from_entity(entity) {
        const direct_user_id = Number(entity?.applicant_user_id || entity?.profile_user_id || 0);
        if (Number.isFinite(direct_user_id) && direct_user_id > 0) {
          return direct_user_id;
        }

        const profile_user = entity.profile_role?.profile?.user;
        if (profile_user?.id) return profile_user.id;

        if (entity.profile_role?.profile?.user_id) {
          return entity.profile_role.profile.user_id;
        }
        if (entity.entity_type?.user_id) {
          return entity.entity_type.user_id;
        }

        return null;
      },

    prep_tables() {
      if (this.pending_applications_list.length > 0) {
        this.sanitized_pending_applications_list = [];
        this.application_row_map = new Map();

        const backend_filter = this._normalize_status_filter(this.selected_status);

        const has_master_registered = this._collection_has_master(this.pending_applications_list);

        this.table_headers = this._build_headers(has_master_registered);

        this.sanitized_pending_applications_list = this.pending_applications_list
          .filter(app => this.should_include_application(app, backend_filter))
          .map((app, index) => {
            const formatted = this.format_application(app, has_master_registered, index);

            if (formatted && Reflect.get(formatted, "Application No")) {
              Reflect.set(
                this.application_row_map,
                String(Reflect.get(formatted, "Application No")),
                formatted
              );
            }

            return formatted;
          });

        this.apply_application_filters();
      } else {
        const display_label = this._get_display_name_label();
        this.table_headers = [
          "Full Name",
          `${display_label} Type`,
          "Date Created",
          "Status"
        ];
        this.displayed_applications_list = [];
      }
    },

    _normalize_status_filter(selected_status) {
      return selected_status
        ? selected_status.toLowerCase().replace(/-/g, "_")
        : null;
    },

    _collection_has_master(apps) {
      return apps.some(app => {
        const status = this.get_application_status_name(app).toLowerCase();
        return status.includes("master registered") || status.includes("master_registered");
      });
    },

    _build_headers(has_master_registered) {
      const selection_grid_headers = this.get_selection_grid_headers();
      return has_master_registered
        ? [...selection_grid_headers, "Trust Number"]
        : selection_grid_headers;
    },

    _get_display_name_label() {
      return this.display_name || "Entity";
    },

    get_table_headers(applications) {
      if (!applications || applications.length === 0) {
        return [];
      }

      const display_label = this._get_display_name_label();
      const base_headers = [
        "Full Name",
        `${display_label} Type`,
        "Date Created",
        "Status"
      ];

      const has_master_registered_with_trust = applications.some(app => {
        const id = String(Reflect.get(app || {}, "Application No"));
        const raw_app = Reflect.get(this.application_row_map, id);

        const raw_status =
          this.get_application_status_name(raw_app) ||
          app.Status ||
          "";

        const is_master_registered = raw_status.toLowerCase().includes("master registered");
        const has_trust_number =
          raw_app?.trust_number ||
          Reflect.get(app || {}, "Trust Number");

        return is_master_registered && has_trust_number;
      });

      if (has_master_registered_with_trust) {
        return [...base_headers, "Trust Number"];
      }

      return base_headers;
    },

    // Extract filter logic to reduce complexity
    should_include_application(application, backend_filter) {
      if (!backend_filter) return true;
      const raw_status = this.get_application_status_name(application).toLowerCase();
      return raw_status === backend_filter;
    },

    format_application(application, has_master_registered, row_index = 0) {
      const metadata = this._extract_metadata(application);
      this._set_app_metadata(application.id, metadata);

      const formatted_application = this._build_base_application(
        application,
        this._get_display_status(application),
        row_index
      );

      if (has_master_registered) {
        this._set_trust_number(formatted_application, metadata);
      }

      return formatted_application;
    },

    _extract_metadata(application) {
      const meta_data = {
        profile_role_id: this.get_valid_profile_role_id(application.id),
        type_id: this.get_application_entity_type_id(application),
        entity_profile_id: application.entity_profile_id ?? application.entity ?? null,
        profile_user_id: this.get_user_id_from_entity(application),
        trust_number: application.trust_number || "",
        is_master_registered: this._check_master_status(this.get_application_status_name(application)),
        ...application
      };
      return meta_data;
    },

    _get_display_status(application) {
      return this.get_application_status_name(application).replace(/_/g, " ");
    },

    _set_trust_number(formatted_application, metadata) {
      Reflect.set(
        formatted_application,
        "Trust Number",
        metadata.is_master_registered ? metadata.trust_number : ""
      );
    },

    _check_master_status(raw_status) {
      const s = raw_status.toLowerCase();
      return s.includes("master registered") || s.includes("master_registered");
    },

    _set_app_metadata(id, metadata) {
      Reflect.set(this.application_row_map, id, metadata);
    },

    hide_application_no_column(row) {
      const application_no = Reflect.get(row || {}, "Application No");
      const display_row = { ...(row || {}) };

      delete display_row["Application No"];

      Object.defineProperty(display_row, "Application No", {
        value: application_no,
        enumerable: false,
        configurable: true,
        writable: true,
      });

      return display_row;
    },

    get_selection_grid_headers() {
      return [
        "No",
        "Surname and Initials",
        "Race",
        "Gender (M/F)",
        "Disability (Y/N)",
        "Criminal record (Y/N)",
        "New Z83 attached, duly completed, initialed and signed (Y/N)",
        "CV attached (Y/N)",
        "Nyukela certificate attached (only SMS post)",
        "Relevant Qualification",
        "Current/relevant Experience",
        this.get_required_qualification_header(),
        this.get_required_experience_header(),
        this.get_selection_pool_header(),
      ];
    },

    get_required_qualification_header() {
      return this.build_requirement_header(
        "Required Qualifications",
        "qualification"
      );
    },

    get_required_experience_header() {
      return this.build_requirement_header(
        "Required Experience",
        "experience"
      );
    },

    build_requirement_header(base_label, requirement_type = "qualification") {
      const requirement_summary = this.get_current_advert_requirement_summary(requirement_type);
      const formatted_summary = this.format_selection_grid_value(requirement_summary);

      if (!formatted_summary || formatted_summary === "-") {
        return `${base_label}:\n(Y/N)`;
      }

      return `${base_label}:\n${formatted_summary}\n(Y/N)`;
    },
    get_fallback_selected_application() {
      return (this.pending_applications_list || []).find((application) => {
        if (!this.advert_id) {
          return true;
        }
        return this.get_application_advert_id(application) === Number(this.advert_id);
      }) || this.pending_applications_list?.[0] || null;
    },
    find_selected_job_advert_by_id() {
      if (!this.advert_id) return null;
      return (this.job_adverts_list || []).find((advert) => {
        const candidate_id = Number(advert?.id || advert?.advert_id || 0);
        return candidate_id === Number(this.advert_id);
      }) || null;
    },
    get_fallback_job_advert_from_application() {
      const selected_application = this.get_fallback_selected_application();
      return selected_application ? this.get_job_advert_for_application(selected_application) : null;
    },

    get_selected_job_advert() {
      return this.find_selected_job_advert_by_id() || this.get_fallback_job_advert_from_application();
    },

    get_current_advert_requirement_summary(requirement_type = "qualification") {
      const selected_advert = this.get_selected_job_advert();
      if (selected_advert) {
        const section_summary = this.format_selection_grid_bullet_lines(
          this.get_section_requirement_items(selected_advert, requirement_type)
        );
        if (section_summary) {
          return section_summary;
        }
      }

      const selected_application = this.get_fallback_selected_application();

      if (!selected_application) {
        return "";
      }

      return this.get_advert_requirement_summary(selected_application, requirement_type);
    },

    get_selection_pool_header() {
      return [
        "Results of preliminary selection pool",
        "A = Applicant does not meet the minimum qualification requirements for the post.",
        "B = Applicant does not meet the required experience for the post.",
      ].join("\n");
    },

    _build_base_application(application, _display_status, row_index = 0) {
      const entity_name = (application?.entity_name || "").trim();

      const name =
        entity_name ||
        (application?.applicant_name || "").toString().trim() ||
        this.get_application_user_name(application) ||
        "Entity not yet onboarded";
      const orcaa_score_result = this.get_orcaa_score_result(application);
      const relevant_qualification = this.get_relevant_qualification_for_grid(application) || this.get_selection_grid_value(application, [
        "relevant qualification",
        "qualification",
        "applicant qualifications",
        "education",
        "formal qualification",
      ]);
      const relevant_experience = this.get_work_experience_for_grid(application) || this.get_selection_grid_value(application, [
        "current relevant experience",
        "current/relevant experience",
        "relevant experience",
        "work experience",
        "experience",
      ]);
      const required_qualification = this.to_yes_no_grid_value(
        orcaa_score_result?.meets_qualification_requirement
      ) || this.get_selection_grid_value(application, [
        "required qualifications",
        "meets required qualification",
        "meets qualification requirement",
        "required qualification",
        "qualification requirement",
      ], { yes_no: true, placeholder: "" }) || this.infer_requirement_match_from_advert(
        application,
        relevant_qualification,
        [
          "required qualifications",
          "required qualification",
          "minimum qualifications",
          "minimum qualification",
          "qualification requirements",
          "qualification requirement",
        ]
      );
      const required_experience = this.to_yes_no_grid_value(
        orcaa_score_result?.meets_experience_requirement
      ) || this.get_selection_grid_value(application, [
        "required experience",
        "meets required experience",
        "meets experience requirement",
        "experience requirement",
        "minimum experience",
      ], { yes_no: true, placeholder: "" }) || this.infer_requirement_match_from_advert(
        application,
        relevant_experience,
        [
          "required experience",
          "minimum experience",
          "experience requirement",
          "experience requirements",
          "years of experience",
        ]
      );
      const normalized_assessment = this.normalize_threshold_assessment(
        this.get_threshold_assessment(application)
      );
      const display_required_qualification = this.normalize_grid_yes_no(
        required_qualification,
        normalized_assessment === "unqualified" ? "N" : "-"
      );
      const display_required_experience = this.normalize_grid_yes_no(
        required_experience,
        normalized_assessment === "unqualified" ? "N" : "-"
      );
      const document_checks = {
        z83: this.get_selection_grid_document_value(application, [
          "z83",
          "new z83",
          "z83 attached",
          "z83 form",
          "preview the z83 application form",
        ]),
        cv: this.get_selection_grid_document_value(application, [
          "cv attached",
          "cv",
          "curriculum vitae",
          "resume",
        ]),
      };

      const row = {
        "Application No": application.id,
        "No": row_index + 1,
        "Surname and Initials": this.get_surname_initials_for_grid(application) || this.get_selection_grid_value(application, [
          "surname and initials",
          "surname",
          "full name",
        ], { placeholder: "" }) || name,
        "Race": this.get_selection_grid_value(application, ["race"]),
        "Gender (M/F)": this.normalize_gender_for_grid(
          this.get_selection_grid_value(application, ["gender", "sex"])
        ),
        "Disability (Y/N)": this.get_selection_grid_value(application, [
          "disability",
          "disabled",
        ], { yes_no: true }),
        "Criminal record (Y/N)": this.get_selection_grid_value(application, [
          "criminal record",
          "criminality",
          "criminal offence",
          "pending criminal",
        ], { yes_no: true }),
        "New Z83 attached, duly completed, initialed and signed (Y/N)": document_checks.z83,
        "CV attached (Y/N)": document_checks.cv,
        "Nyukela certificate attached (only SMS post)": this.get_selection_grid_value(application, [
          "nyukela",
          "sms certificate",
        ], { yes_no: true }),
        "Relevant Qualification": relevant_qualification,
        "Current/relevant Experience": relevant_experience,
        [this.get_required_qualification_header()]: display_required_qualification,
        [this.get_required_experience_header()]: display_required_experience,
        [this.get_selection_pool_header()]: this.get_selection_result_codes(
          application,
          display_required_qualification,
          display_required_experience
        ),
      };

      const hidden_values = {
        "Full Name": name,
        "Job Applied For": `${this.get_application_entity_type_name(application) || "Entity"}`,
        "Advert": (application?.advert_title || "").toString().trim() || "-",
        "Date Created": application.date_created,
        "Status": _display_status,
      };

      Object.entries(hidden_values).forEach(([key, value]) => {
        Object.defineProperty(row, key, {
          value,
          enumerable: false,
          configurable: true,
          writable: true,
        });
      });

      return row;
    },

    get_selection_answer_rows(application) {
      return [
        ...(Array.isArray(application?.selection_grid_answers) ? application.selection_grid_answers : []),
        ...(Array.isArray(application?.document_vault_documents) ? application.document_vault_documents : []),
        ...(Array.isArray(application?.attributes) ? application.attributes : []),
        ...(Array.isArray(application?.current_entity_attributes) ? application.current_entity_attributes : []),
      ];
    },

    get_answer_label(attribute) {
      return (
        attribute?.attribute_name ||
        attribute?.question_text ||
        attribute?.question ||
        attribute?.file_name ||
        attribute?.document_name ||
        attribute?.name ||
        ""
      );
    },

    get_answer_value(attribute) {
      return (
        attribute?.value ??
        attribute?.attribute_values ??
        attribute?.answer ??
        attribute?.file_url ??
        attribute?.presigned_url ??
        attribute?.signed_file_url ??
        attribute?.file_name ??
        null
      );
    },

    get_application_advert_id(application) {
      const candidates = [
        application?.advert_id,
        application?.job_advert_id,
        application?.advert?.id,
        application?.advert?.advert_id,
        application?.job_advert?.id,
        application?.job_advert?.advert_id,
      ];
      return normalize_positive_number(candidates.find((value) => value));
    },
    find_list_job_advert(advert_id) {
      return advert_id
        ? (this.job_adverts_list || []).find((advert) => {
            const candidate_id = Number(advert?.id || advert?.advert_id || 0);
            return candidate_id === advert_id;
          }) || null
        : null;
    },
    advert_has_display_content(advert) {
      return !!(advert && (
        (Array.isArray(advert?.sections) && advert.sections.length > 0) ||
        this.format_selection_grid_value(advert?.details)
      ));
    },

    get_job_advert_for_application(application) {
      const advert_id = this.get_application_advert_id(application);
      const list_advert = this.find_list_job_advert(advert_id);
      if (this.advert_has_display_content(list_advert)) {
        return list_advert;
      }
      if (application?.advert && typeof application.advert === "object") {
        return application.advert;
      }
      return list_advert;
    },
    extract_advert_text_value(value) {
      return String(value)
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    collect_fragment_from_object(value, fragments) {
      this.push_defined_advert_fragment(fragments, this.get_preferred_advert_fragment(value));
      this.push_advert_object_fragment_arrays(value, fragments);
      this.push_advert_table_fragments(fragments, value.table);
    },
    push_advert_object_fragment_arrays(value, fragments) {
      this.get_advert_object_fragment_arrays(value).forEach((items) => {
        this.push_advert_array_fragment(fragments, items);
      });
    },
    get_preferred_advert_fragment(value) {
      return [value.title, value.heading, value.label, value.name, value.content, value.description, value.value]
        .find((item) => item !== null && typeof item !== "undefined");
    },
    get_advert_object_fragment_arrays(value) {
      return [value.descriptions, value.rows, value.headers];
    },
    push_defined_advert_fragment(fragments, value) {
      if (value !== null && typeof value !== "undefined") this.push_advert_text_fragment(fragments, value);
    },
    push_advert_array_fragment(fragments, value) {
      if (Array.isArray(value)) value.forEach((item) => { this.push_advert_text_fragment(fragments, item); });
    },
    push_advert_table_fragments(fragments, table) {
      if (!table) return;
      this.push_advert_text_fragment(fragments, table.headers);
      this.push_advert_text_fragment(fragments, table.rows);
    },
    push_advert_text_fragment(fragments, value) {
      if (value === null || typeof value === "undefined") return;
      this.push_present_advert_text_fragment(fragments, value);
    },
    push_present_advert_text_fragment(fragments, value) {
      if (Array.isArray(value)) return this.push_advert_array_fragment(fragments, value);
      this.push_non_array_advert_text_fragment(fragments, value);
    },
    push_non_array_advert_text_fragment(fragments, value) {
      if (typeof value === "object") {
        this.collect_fragment_from_object(value, fragments);
        return;
      }
      this.push_formatted_advert_fragment(fragments, value);
    },
    push_formatted_advert_fragment(fragments, value) {
      const formatted = this.extract_advert_text_value(value);
      if (formatted) fragments.push(formatted);
    },
    get_advert_text_fragment_sources(advert) {
      return [
        advert?.details,
        advert?.description,
        advert?.requirements,
        advert?.minimum_requirements,
        advert?.required_qualifications,
        advert?.required_qualification,
        advert?.required_experience,
        advert?.experience_requirements,
      ];
    },

    collect_advert_text_fragments(advert) {
      if (!advert || typeof advert !== "object") {
        return [];
      }

      const fragments = [];
      this.get_advert_text_fragment_sources(advert)
        .forEach((value) => { this.push_advert_text_fragment(fragments, value); });

      this.flatten_advert_sections(advert?.sections).forEach((section) => {
        this.push_advert_text_fragment(fragments, section?.title);
        this.push_advert_text_fragment(fragments, section?.heading);
        this.push_advert_text_fragment(fragments, section?.content);
        this.push_advert_text_fragment(fragments, section?.description);
        this.push_advert_text_fragment(fragments, section?.descriptions);
        this.push_advert_text_fragment(fragments, section?.table?.headers);
        this.push_advert_text_fragment(fragments, section?.table?.rows);
      });

      return this.dedupe_selection_grid_items(fragments);
    },
    append_flattened_advert_section(flattened, section) {
      if (!section || typeof section !== "object") return;
      flattened.push(section);
      this.flatten_advert_section_items(section.subsections, flattened);
    },
    flatten_advert_section_items(items, flattened) {
      if (!Array.isArray(items)) return;
      items.forEach((section) => { this.append_flattened_advert_section(flattened, section); });
    },

    flatten_advert_sections(sections = []) {
      const flattened = [];
      this.flatten_advert_section_items(sections, flattened);
      return flattened;
    },

    get_advert_requirement_text(application, labels = []) {
      const advert = this.get_job_advert_for_application(application);
      if (!advert) return "";

      const normalized_labels = labels.map(label => this.normalize_selection_label(label)).filter(Boolean);
      if (!normalized_labels.length) return "";
      const direct_matches = this.get_direct_advert_requirement_matches(advert, normalized_labels);
      const section_matches = this.get_section_advert_requirement_matches(advert, normalized_labels);
      const content_matches = this.get_content_advert_requirement_matches(advert, normalized_labels);

      return this.format_selection_grid_bullet_lines([
        ...direct_matches,
        ...section_matches,
        ...content_matches,
      ]);
    },
    get_direct_advert_requirement_matches(advert, normalized_labels) {
      return Object.entries(advert).map(([key, value]) => {
        if (!this.normalized_label_matches(this.normalize_selection_label(key), normalized_labels)) return "";
        return this.format_selection_grid_value(value);
      }).filter(Boolean);
    },
    get_section_advert_requirement_matches(advert, normalized_labels) {
      return this.flatten_advert_sections(advert?.sections)
        .map((section) => this.get_matching_advert_section_content(section, normalized_labels))
        .filter(Boolean);
    },
    get_matching_advert_section_content(section, normalized_labels) {
      const normalized_title = this.normalize_selection_label(this.format_selection_grid_value(section?.title || section?.heading));
      const section_content = this.get_advert_section_content(section);
      if (!section_content) return "";
      if (this.normalized_label_matches(normalized_title, normalized_labels)) return section_content;
      return this.get_generic_requirement_section_content(normalized_title, section_content, normalized_labels);
    },
    get_generic_requirement_section_content(normalized_title, section_content, normalized_labels) {
      if (!normalized_title.includes("requirement")) return "";
      const normalized_content = this.normalize_selection_label(section_content);
      return this.normalized_label_matches(normalized_content, normalized_labels) ? section_content : "";
    },
    get_advert_section_content(section) {
      return this.format_selection_grid_bullet_lines([
        section?.content,
        section?.description,
        ...(Array.isArray(section?.descriptions) ? section.descriptions : []),
        ...(Array.isArray(section?.table?.headers) ? section.table.headers : []),
        ...(Array.isArray(section?.table?.rows) ? section.table.rows : []),
      ]);
    },
    get_content_advert_requirement_matches(advert, normalized_labels) {
      return this.collect_advert_text_fragments(advert).filter((fragment) =>
        this.normalized_label_matches(this.normalize_selection_label(fragment), normalized_labels));
    },
    normalized_label_matches(value, normalized_labels) {
      return !!value && normalized_labels.some((label) => value.includes(label));
    },

    infer_requirement_match_from_advert(application, applicant_value, advert_requirement_labels = []) {
      const requirement_text = this.get_advert_requirement_text(application, advert_requirement_labels);
      if (!requirement_text) {
        return "";
      }

      const formatted_applicant_value = this.format_selection_grid_value(applicant_value);
      return formatted_applicant_value && formatted_applicant_value !== "-" ? "Y" : "N";
    },

    get_requirement_lines_from_text(value) {
      return this.split_selection_grid_bullet_items([value]).map((item) => item.trim()).filter(Boolean);
    },

    is_qualification_requirement_line(value) {
      const normalized = this.normalize_selection_label(value);
      return [
        "qualification",
        "qualifications",
        "degree",
        "diploma",
        "certificate",
        "matric",
        "grade 12",
        "nqf",
        "education",
        "field of study",
      ].some((keyword) => normalized.includes(keyword));
    },

    is_experience_requirement_line(value) {
      const normalized = this.normalize_selection_label(value);
      return [
        "experience",
        "years",
        "year",
        "worked",
        "employment",
        "exposure",
      ].some((keyword) => normalized.includes(keyword));
    },

    section_title_matches_requirement_type(section, requirement_type = "qualification") {
      const title = this.normalize_selection_label(section?.title || section?.heading || "");
      if (!title) return false;
      return this.title_matches_requirement_type(title, requirement_type);
    },
    title_matches_requirement_type(title, requirement_type) {
      return requirement_type === "experience" ? title.includes("experience") : this.title_matches_qualification_requirement(title);
    },
    title_matches_qualification_requirement(title) {
      return ["minimum requirement", "minimum requirements", "qualification", "requirement"]
        .some((keyword) => title.includes(keyword));
    },

    get_matching_requirement_sections(advert, requirement_type = "qualification") {
      const sections = this.flatten_advert_sections(advert?.sections);
      return sections.filter((section) =>
        this.section_title_matches_requirement_type(section, requirement_type)
      );
    },

    get_section_requirement_items(advert, requirement_type = "qualification") {
      const matching_sections = this.get_matching_requirement_sections(advert, requirement_type);
      if (!matching_sections.length) {
        return [];
      }

      const items = matching_sections.flatMap((section) => this.get_requirement_lines_from_text([
        section?.content,
        section?.description,
        ...(Array.isArray(section?.descriptions) ? section.descriptions : []),
        ...(Array.isArray(section?.table?.headers) ? section.table.headers : []),
        ...(Array.isArray(section?.table?.rows) ? section.table.rows : []),
      ]));

      return this.dedupe_selection_grid_items(items);
    },

    get_advert_requirement_items(application, requirement_type = "qualification") {
      const advert = this.get_job_advert_for_application(application);
      if (!advert) return [];

      const section_items = this.get_section_requirement_items(advert, requirement_type);
      if (section_items.length) return section_items;

      const requirement_text = this.get_advert_requirement_text(application, this.get_requirement_text_labels(requirement_type));
      if (!requirement_text) return [];
      return this.get_filtered_requirement_items(requirement_text, requirement_type);
    },
    get_requirement_text_labels(requirement_type) {
      return [
        "minimum requirements",
        "minimum requirement",
        "requirements",
        "requirement",
        ...(requirement_type === "qualification"
          ? ["required qualifications", "required qualification", "qualification requirement"]
          : ["required experience", "experience requirement", "minimum experience"]),
      ];
    },
    get_filtered_requirement_items(requirement_text, requirement_type) {
      const requirement_lines = this.get_requirement_lines_from_text(requirement_text);
      const filtered_lines = requirement_lines.filter((line) => this.requirement_line_matches_type(line, requirement_type));
      return this.dedupe_selection_grid_items(filtered_lines);
    },
    requirement_line_matches_type(line, requirement_type) {
      return requirement_type === "qualification"
        ? this.is_qualification_requirement_line(line)
        : this.is_experience_requirement_line(line);
    },

    get_advert_requirement_summary(application, requirement_type = "qualification") {
      return this.format_selection_grid_bullet_lines(
        this.get_advert_requirement_items(application, requirement_type)
      );
    },

    combine_requirement_status_and_text(status, requirement_text) {
      const formatted_status = this.format_selection_grid_value(status) || "-";
      const formatted_text = this.format_selection_grid_value(requirement_text);
      if (!formatted_text || formatted_text === "-") {
        return formatted_status;
      }
      return `${formatted_status}\n${formatted_text}`;
    },

    get_surname_initials_for_grid(application) {
      const surname = this.get_selection_grid_value(application, ["surname"], { placeholder: "" });
      const initials = this.get_selection_grid_value(application, ["initials"], { placeholder: "" });

      if (surname && initials) {
        return `${surname} ${initials}`;
      }

      return surname || "";
    },

    get_direct_selection_values(application, labels = []) {
      const matched_values = [];
      const seen = new Set();
      this.get_direct_selection_sources(application).forEach((source) => {
        this.push_direct_selection_values(source, labels, seen, matched_values);
      });
      return matched_values;
    },
    get_direct_selection_sources(application) {
      return [
        application,
        application?.advert,
        application?.ai_selection_grid,
        application?.ai_assessment,
        application?.fields,
        application?.orcaa_response?.score_result,
        application?.orcaa_response?.generated_work?.score_result,
        application?.recruitment_assessment,
        application?.compliance_summary,
      ];
    },
    push_direct_selection_values(source, labels, seen, matched_values) {
      if (!source || typeof source !== "object") return;
      Object.entries(source).forEach(([key, value]) => {
        this.push_matching_direct_selection_value(key, value, labels, seen, matched_values);
      });
    },
    push_matching_direct_selection_value(key, value, labels, seen, matched_values) {
      if (!this.direct_selection_key_matches(key, labels)) return;
      const formatted_value = this.format_selection_grid_value(value);
      const normalized_value = this.normalize_selection_label(formatted_value);
      if (!this.can_push_direct_selection_value(formatted_value, normalized_value, seen)) return;
      seen.add(normalized_value);
      matched_values.push(formatted_value);
    },
    can_push_direct_selection_value(formatted_value, normalized_value, seen) {
      return !!formatted_value && !!normalized_value && !seen.has(normalized_value);
    },
    direct_selection_key_matches(key, labels) {
      const normalized_key = this.normalize_selection_label(key);
      return labels.some(label => normalized_key.includes(this.normalize_selection_label(label)));
    },

    get_relevant_qualification_for_grid(application) {
      const advert_specific_values = this.get_direct_selection_values(application, [
        "relevant qualification",
        "relevant qualifications",
        "qualification relevant to the advert",
        "matched qualification",
        "qualification match",
      ]);

      if (advert_specific_values.length) {
        return this.format_selection_grid_bullet_lines(advert_specific_values);
      }

      const rows = this.get_selection_answer_rows(application)
        .filter(attribute => {
          const label = this.normalize_selection_label(this.get_answer_label(attribute));
          return this.is_qualification_name_label(label);
        })
        .map(attribute => this.format_selection_grid_value(this.get_answer_value(attribute)))
        .filter(Boolean);

      if (rows.length) {
        return this.format_selection_grid_bullet_lines(this.dedupe_selection_grid_items(rows));
      }

      const grouped_qualifications = this.get_grouped_qualifications(application);
      const formatted_groups = grouped_qualifications
        .map(qualification => this.format_qualification_group(qualification))
        .filter(Boolean);

      return this.format_selection_grid_bullet_lines(this.dedupe_selection_grid_items(formatted_groups));
    },

    get_grouped_qualifications(application) {
      const groups = new Map();

      this.get_selection_answer_rows(application).forEach(attribute => {
        const raw_label = this.get_answer_label(attribute);
        const normalized_label = this.normalize_selection_label(raw_label);
        const value = this.format_selection_grid_value(this.get_answer_value(attribute));
        const group_id = this.get_qualification_group_id(raw_label);

        if (!group_id || !value) {
          return;
        }

        if (!groups.has(group_id)) {
          groups.set(group_id, {});
        }

        const group = groups.get(group_id);
        if (
          normalized_label.includes("school technical college")
          || normalized_label.includes("institution")
          || normalized_label.includes("university")
          || normalized_label.includes("college")
        ) {
          group.institution = value;
        } else if (
          normalized_label.includes("name of qualification")
          || normalized_label.includes("qualification obtained")
          || normalized_label.includes("qualification name")
          || normalized_label.includes("qualification title")
          || normalized_label === "qualification"
        ) {
          group.qualification = value;
        } else if (
          normalized_label.includes("year qualification")
          || normalized_label.includes("year obtained")
          || normalized_label.includes("completion year")
          || normalized_label.includes("year completed")
        ) {
          group.year = value;
        } else if (normalized_label.includes("nqf")) {
          group.nqf_level = value;
        } else if (
          normalized_label.includes("field of study")
          || normalized_label.includes("major")
          || normalized_label.includes("specialisation")
          || normalized_label.includes("specialization")
          || normalized_label.includes("course")
        ) {
          group.field = value;
        } else if (
          normalized_label.includes("qualification type")
          || normalized_label.includes("certificate")
          || normalized_label.includes("diploma")
          || normalized_label.includes("degree")
        ) {
          group.type = value;
        } else if (this.is_relevant_qualification_label(normalized_label)) {
          if (!group.other) {
            group.other = [];
          }
          group.other.push(this.format_labeled_selection_grid_value(raw_label, value));
        }
      });

      return Array.from(groups.values());
    },

    get_qualification_group_id(label) {
      const raw_label = (label || "").toString().trim();
      const edu_match = raw_label.match(/^\[EDU\s*(\d+)\]/i);
      if (edu_match) {
        return edu_match[1];
      }

      const numbered_match = raw_label.match(/(?:education|edu|college|qualification|obtained|level|institution|nqf|course|degree|diploma|certificate)\s*(\d+)/i);
      return numbered_match ? numbered_match[1] : "";
    },

    format_qualification_group(qualification) {
      return qualification.qualification || "";
    },

    is_qualification_name_label(normalized_label) {
      if (
        normalized_label.includes("required qualification")
        || normalized_label.includes("qualification requirement")
        || normalized_label.includes("relevant qualification")
      ) {
        return false;
      }

      return (
        normalized_label.includes("name of qualification")
        || normalized_label.includes("qualification obtained")
        || normalized_label.includes("qualification name")
        || normalized_label.includes("qualification title")
        || normalized_label === "qualification"
        || normalized_label.includes("highest grade")
        || normalized_label.includes("highest school")
        || normalized_label.includes("school grade")
        || normalized_label.includes("grade passed")
        || normalized_label.includes("matric")
      );
    },

    is_relevant_qualification_label(normalized_label) {
      return (
        normalized_label.includes("qualification")
        || normalized_label.includes("education")
        || normalized_label.includes("institution")
        || normalized_label.includes("school technical college")
        || normalized_label.includes("university")
        || normalized_label.includes("college")
        || normalized_label.includes("nqf")
        || normalized_label.includes("field of study")
        || normalized_label.includes("major")
        || normalized_label.includes("course")
        || normalized_label.includes("degree")
        || normalized_label.includes("diploma")
        || normalized_label.includes("certificate")
      );
    },

    format_labeled_selection_grid_value(label, value) {
      const cleaned_label = (label || "")
        .toString()
        .replace(/^\[EDU\s*\d+\]\s*/i, "")
        .trim();
      if (!cleaned_label) {
        return value;
      }
      return `${cleaned_label}: ${value}`;
    },

    get_work_experience_for_grid(application) {
      const advert_specific_values = this.get_direct_selection_values(application, [
        "current relevant experience",
        "current/relevant experience",
        "relevant experience",
        "experience relevant to the advert",
        "matched experience",
        "experience match",
      ]);

      if (advert_specific_values.length) {
        return this.format_selection_grid_bullet_lines(advert_specific_values);
      }

      const grouped_experience = this.get_grouped_work_experience(application);
      const formatted_groups = grouped_experience
        .map(experience => this.format_work_experience_group(experience))
        .filter(Boolean);

      if (formatted_groups.length) {
        return this.format_selection_grid_bullet_lines(this.dedupe_selection_grid_items(formatted_groups));
      }

      const rows = this.get_selection_answer_rows(application)
        .filter(attribute => this.normalize_selection_label(this.get_answer_label(attribute)).includes("we "))
        .map(attribute => {
          const label = this.get_answer_label(attribute).replace(/^\[WE\s*\d+\]\s*/i, "");
          const value = this.format_selection_grid_value(this.get_answer_value(attribute));
          return value ? `${label}: ${value}` : "";
        })
        .filter(Boolean);

      return this.format_selection_grid_bullet_lines(this.dedupe_selection_grid_items(rows));
    },

    format_selection_grid_bullet_lines(items = []) {
      return this.dedupe_selection_grid_items(this.split_selection_grid_bullet_items(items))
        .map(item => `* ${item.replace(/^\*\s*/, "")}`)
        .filter(Boolean)
        .join("\n");
    },

    split_selection_grid_bullet_items(items = []) {
      return items.flatMap(item => {
        const formatted_item = this.format_selection_grid_value(item);
        if (!formatted_item) {
          return [];
        }

        return formatted_item
          .replace(/\r/g, "\n")
          .replace(/^\*\s*/, "")
          .split(/\n+|\s+\*\s+/)
          .map(part => part.trim())
          .filter(Boolean);
      });
    },

    get_grouped_work_experience(application) {
      const groups = new Map();

      this.get_selection_answer_rows(application).forEach(attribute => {
        const raw_label = this.get_answer_label(attribute);
        const match = raw_label.match(/^\[WE\s*(\d+)\]\s*(.+)$/i);
        if (!match) {
          return;
        }

        const group_id = match[1];
        const field_label = this.normalize_selection_label(match[2]);
        const value = this.format_selection_grid_value(this.get_answer_value(attribute));
        if (!value) {
          return;
        }

        if (!groups.has(group_id)) {
          groups.set(group_id, {});
        }

        const group = groups.get(group_id);
        if (field_label.includes("employer")) {
          group.employer = value;
        } else if (field_label.includes("post held") || field_label.includes("position") || field_label.includes("job title")) {
          group.post_held = value;
        } else if (field_label.includes("from month") || field_label.includes("start month")) {
          group.from_month = value;
        } else if (field_label.includes("from year") || field_label.includes("start year")) {
          group.from_year = value;
        } else if (field_label.includes("to month") || field_label.includes("end month")) {
          group.to_month = value;
        } else if (field_label.includes("to year") || field_label.includes("end year")) {
          group.to_year = value;
        } else if (
          field_label.includes("current")
          || field_label.includes("present")
          || field_label.includes("currently employed")
          || field_label.includes("still employed")
        ) {
          group.current = value;
        }
      });

      return Array.from(groups.values());
    },

    format_work_experience_group(experience) {
      const employer = (experience.employer || "").trim();
      const post_held = (experience.post_held || "").trim();
      const from_date = [experience.from_month, experience.from_year].filter(Boolean).join(" ");
      const has_current_end = this.is_checked_selection_grid_value(experience.current);
      const has_saved_end_date = Boolean(experience.to_month || experience.to_year);
      const to_date = [experience.to_month, experience.to_year].filter(Boolean).join(" ");
      const date_range = has_current_end || !has_saved_end_date
        ? [from_date, "date"].filter(Boolean).join(" to ")
        : [from_date, to_date].filter(Boolean).join(" to ");
      const parts = [];

      if (date_range && (has_current_end || has_saved_end_date || from_date)) {
        parts.push(date_range);
      }
      if (employer) {
        parts.push(employer);
      }
      if (post_held) {
        parts.push(post_held);
      }

      return parts.join(", ");
    },

    is_checked_selection_grid_value(value) {
      const formatted = this.format_selection_grid_value(value).toLowerCase();
      return [
        "y",
        "yes",
        "true",
        "1",
        "checked",
        "ticked",
        "tick",
        "current",
        "present",
        "to date",
        "currently employed",
        "still employed",
      ].includes(formatted);
    },

    dedupe_selection_grid_items(items = []) {
      const seen = new Set();
      return items.filter(item => {
        const normalized_item = this.normalize_selection_label(item);
        if (!normalized_item || seen.has(normalized_item)) {
          return false;
        }
        seen.add(normalized_item);
        return true;
      });
    },

    get_orcaa_score_result(application) {
      const candidates = this.get_orcaa_score_candidates(application);
      return candidates.find((candidate) => this.is_orcaa_score_candidate(candidate)) || null;
    },
    get_orcaa_score_candidates(application) {
      const candidates = [
        application?.fields,
        application?.orcaa_response?.score_result,
        application?.orcaa_response?.generated_work?.score_result,
        application?.score_result,
        application?.ai_assessment?.score_result,
        application?.recruitment_assessment?.score_result,
      ];
      return candidates;
    },
    is_orcaa_score_candidate(candidate) {
      if (!candidate || typeof candidate !== "object") return false;
      return ["fit_recommendation", "meets_qualification_requirement", "meets_experience_requirement"]
        .some((key) => Reflect.has(candidate, key));
    },

    get_selection_grid_value(application, labels = [], options = {}) {
      const direct_value = this.get_direct_selection_value(application, labels);
      const attribute_value = direct_value || this.get_attribute_selection_value(application, labels);
      const normalized_value = options.yes_no
        ? this.to_yes_no_grid_value(attribute_value)
        : this.format_selection_grid_value(attribute_value);

      if (normalized_value) {
        return normalized_value;
      }

      if (Reflect.has(options, "placeholder")) {
        return options.placeholder;
      }

      return "-";
    },

    get_selection_grid_document_value(application, labels = []) {
      const direct_value = this.get_direct_selection_value(application, labels);
      if (this.has_document_evidence(direct_value)) {
        return "Y";
      }

      const attributes = this.get_selection_answer_rows(application);
      for (const attribute of attributes) {
        const attribute_label = this.normalize_selection_label(this.get_answer_label(attribute));
        const answer_label = this.normalize_selection_label(this.get_answer_value(attribute));
        const matches_document_label = labels.some(label => {
          const normalized_label = this.normalize_selection_label(label);
          return (
            attribute_label.includes(normalized_label) ||
            answer_label.includes(normalized_label)
          );
        });
        if (!matches_document_label) {
          continue;
        }

        if (this.has_document_evidence(this.get_answer_value(attribute), attribute)) {
          return "Y";
        }
      }

      return "N";
    },

    get_answer_type(attribute) {
      return (
        attribute?.attribute_type ||
        attribute?.question_type ||
        attribute?.type ||
        attribute?.question?.question_type?.type ||
        attribute?.question?.question_type?.question_type ||
        ""
      ).toString().toLowerCase();
    },

    has_document_evidence(value, attribute = null) {
      if (value === null || typeof value === "undefined") {
        return false;
      }

      const answer_type = this.get_answer_type(attribute);
      const is_file_answer = [
        "file",
        "filetype",
        "file_upload",
        "pdf_file",
        "document",
      ].includes(answer_type);

      if (Array.isArray(attribute?.files) && attribute.files.some(file => this.has_document_evidence(file))) {
        return true;
      }

      if (Array.isArray(value)) {
        return value.some(item => this.has_document_evidence(item, attribute));
      }

      if (typeof value === "object") {
        const file_values = [
          value.url,
          value.file_url,
          value.signed_file_url,
          value.presigned_url,
          value.file_name,
          value.name,
          value.path,
          value.key,
          value.answer,
          value.value,
        ];
        return file_values.some(file_value => this.has_document_evidence(file_value, attribute));
      }

      const formatted = value.toString().trim();
      if (!formatted) {
        return false;
      }

      const normalized = formatted.toLowerCase();
      if ([
        "y",
        "yes",
        "true",
        "1",
        "attached",
        "submitted",
        "complete",
        "completed",
        "i agree",
        "agree",
        "i confirm",
        "confirm",
        "confirmed",
      ].includes(normalized)) {
        return false;
      }

      if (is_file_answer) {
        return true;
      }

      return /https?:\/\/|s3|\/media\/|\\media\\|\.pdf|\.doc|\.docx|\.jpg|\.jpeg|\.png/i.test(formatted);
    },

    get_direct_selection_value(application, labels = []) {
      const direct_sources = [
        application,
        application?.ai_selection_grid,
        application?.ai_assessment,
        application?.fields,
        application?.orcaa_response?.score_result,
        application?.orcaa_response?.generated_work?.score_result,
        application?.recruitment_assessment,
        application?.compliance_summary,
      ];

      for (const source of direct_sources) {
        if (!source || typeof source !== "object") {
          continue;
        }

        for (const [key, value] of Object.entries(source)) {
          const normalized_key = this.normalize_selection_label(key);
          if (labels.some(label => normalized_key.includes(this.normalize_selection_label(label)))) {
            return value;
          }
        }
      }

      return null;
    },

    get_attribute_selection_value(application, labels = []) {
      const attributes = this.get_selection_answer_rows(application);

      for (const attribute of attributes) {
        const attribute_label = this.normalize_selection_label(this.get_answer_label(attribute));

        if (!labels.some(label => attribute_label.includes(this.normalize_selection_label(label)))) {
          continue;
        }

        const value = this.get_answer_value(attribute);
        if (this.format_selection_grid_value(value)) {
          return value;
        }
      }

      return null;
    },

    normalize_selection_label(value) {
      return (value || "")
        .toString()
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    format_selection_grid_value(value) {
      if (value === null || typeof value === "undefined") {
        return "";
      }

      if (Array.isArray(value)) {
        return value.map(item => this.format_selection_grid_value(item)).filter(Boolean).join(", ");
      }

      if (typeof value === "object") {
        const preferred = value.label ?? value.name ?? value.value ?? value.answer ?? value.file_name ?? value.url;
        if (preferred !== null && typeof preferred !== "undefined") {
          return this.format_selection_grid_value(preferred);
        }
        return Object.values(value).map(item => this.format_selection_grid_value(item)).filter(Boolean).join(", ");
      }

      return value
        .toString()
        .replace(/\r/g, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|li|ul|ol|h1|h2|h3|h4|h5|h6)>/gi, "\n")
        .replace(/<li\b[^>]*>/gi, "* ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/\n\s*\n+/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .replace(/[ \t]{2,}/g, " ")
        .trim();
    },

    to_yes_no_grid_value(value) {
      const formatted = this.format_selection_grid_value(value);
      if (!formatted) {
        return "";
      }

      const normalized = formatted.toLowerCase();
      if (["y", "yes", "true", "1", "attached", "submitted", "complete", "completed", "i agree", "agree"].includes(normalized)) {
        return "Y";
      }
      if (["n", "no", "false", "0", "not attached", "missing", "incomplete"].includes(normalized)) {
        return "N";
      }

      if (/https?:\/\/|s3|\.pdf|\.doc|\.docx|\.jpg|\.jpeg|\.png/i.test(formatted)) {
        return "Y";
      }

      return formatted;
    },

    normalize_grid_yes_no(value, fallback = "-") {
      const formatted = this.format_selection_grid_value(value);
      if (!formatted || formatted === "-") {
        return fallback;
      }
      const normalized = this.to_yes_no_grid_value(formatted);
      return normalized || fallback;
    },

    normalize_gender_for_grid(value) {
      const formatted = this.format_selection_grid_value(value);
      const normalized = formatted.toLowerCase();
      if (normalized === "female") {
        return "F";
      }
      if (normalized === "male") {
        return "M";
      }
      return formatted || "-";
    },

    get_selection_result_codes(application, required_qualification, required_experience) {
      const backend_result = this.get_selection_grid_value(application, [
        "results of preliminary selection pool",
        "preliminary selection",
        "selection result",
      ]);
      if (backend_result && backend_result !== "-") {
        return backend_result;
      }

      const codes = [];
      if (required_qualification === "N") {
        codes.push("A");
      }
      if (required_experience === "N") {
        codes.push("B");
      }
      return codes.length ? codes.join(", ") : "-";
    },

    get_advert_badge_label(row, value) {
      const application_id = Number.parseInt(Reflect.get(row || {}, "Application No"), 10);
      const original = Number.isSafeInteger(application_id) ? this.get_application_by_id(application_id) : null;
      const advert_id = Number(original?.advert_id || 0);
      const advert_title = (original?.advert_title || "").toString().trim();
      const fallback = (value || "").toString().trim();

      if (advert_id > 0 && advert_title) {
        return `#${advert_id} | ${advert_title}`;
      }
      if (advert_id > 0) {
        return `#${advert_id}`;
      }
      if (advert_title) {
        return advert_title;
      }
      if (fallback && fallback !== "-") {
        return fallback;
      }
      return "";
    },


    get_application_data(row) {
      if (!row) {
        return null;
      }

      const application_id = Reflect.get(row || {}, "Application No");

      // Validate the application ID
      if (!application_id || (typeof application_id !== 'string' && typeof application_id !== 'number')) {
        return null;
      }

      // Ensure the map exists and is a Map
      if (!(this.application_row_map instanceof Map)) {
        return null;
      }

      const safe_id = String(application_id);

      // Check if it exists inside the Map
      if (!this.application_row_map.has(safe_id)) {
        return null;
      }

      // Return safely via Map.get — no square brackets (Codacy-safe)
      return this.application_row_map.get(safe_id);
    },

    get_valid_profile_role_id(application_id) {
      const profile_role_entry = this.profile_role_ids.find(
        (entry) => entry.application_id === application_id
      );
      return profile_role_entry?.roleId &&
        Number.isInteger(profile_role_entry.roleId) &&
        profile_role_entry.roleId > 0
        ? profile_role_entry.roleId
        : null;
    },

    handle_api_error(message) {
      const errorMsg = message || "Failed to move to master";
      this.show_error(errorMsg);
    },

    get_error_message(error) {
      if (!error.response) {
        return "Failed to move to master submissions";
      }
      this.show_error(error.response.data?.message || "An error occurred while moving to master");
    },

    redirect_to_application_overview(row) {
      // Get ALL data directly from the row object
      const application_id = Number(Reflect.get(row || {}, "Application No"));
      if (!application_id) {
        this.show_error("Unable to find application data");
        return;
      }

      // 2) Get the original application with all meta
      const original_application = this.get_application_by_id(application_id);
      const trust_number = original_application?.trust_number || "";
      if (!original_application) {
        this.show_error("Original application not found");
        return;
      }

      let entity_profile_id =
        original_application.entity_profile_id ??
        original_application.entity ??
        null;

      let entity_type_id =
        original_application.entity_type_id ??
        original_application.entity_type?.id ??
        original_application.type_id ??
        null;

      let entity_name =
        Reflect.get(row || {}, "Entity Name") ||
        original_application.entity_name ||
        this.get_application_user_name(original_application);

      const user_id = original_application.profile_user_id ||
        original_application?.applicant_user_id ||
        null;

      const selected_application = this.pending_applications_list.find(
        app => app.id === application_id
      );

      if (!selected_application) {
        this.show_error("Application not found");
        return;
      }

      const status =
        this.get_application_status_name(selected_application) ||
        selected_application.Status ||
        "";

      // sessionStorage
      sessionStorage.setItem("application_id", application_id);
      sessionStorage.setItem("application_status", status);

      if (!entity_name) {
        entity_name = "Unknown Entity";
      }
      sessionStorage.setItem("entity_name", entity_name);

      if (entity_profile_id != null) {
        sessionStorage.setItem("entity_profile_id", entity_profile_id);
      }

      if (entity_type_id != null) {
        sessionStorage.setItem("entity_type_id", entity_type_id);
      }

      const query_params = {
        application_id,
        status,
        ...(entity_profile_id != null && { entity_profile_id }),
        ...(entity_name && { entity_name }),
        ...(entity_type_id != null && { entity_type_id }),
        ...(user_id && { user_id }),
        ...(trust_number && { trust_number }),
      };

      this.$router.push({
        path: "/overview_of_profile",
        query: query_params,
      });
    },

    delete_application(row) {
      const application_id = Reflect.get(row || {}, "Application No");
      if (!application_id) {
        this.show_error("Unable to find application data");
        return;
      }

      const payload = { application_id: application_id };

      axios.post(DELETE_APPLICATION_URL, payload, { headers: this.headers })
        .then(() => {
          this.view_pending_applications();
          this.show_success("Application deleted successfully");
        })
        .catch(error => {
          this.show_error("Error deleting application: " + (error.response?.data?.error || error.message));
        });
    },

    normalize_item(item) {
      const is_child = Boolean(item.is_child);

      const type_id =
        Number(item.type_id ?? item.entity_type_id ?? item._child_raw?.entity_type_id ?? 0);

      const application_id = Number(
        Reflect.get(item || {}, "Application No") ??
        item.application_id ??
        item._child_raw?.forms?.[0]?.application_id ??
        0
      );

      const entity_profile_id = Number(
        is_child
          ? (item.id ?? item.entity_profile_id ?? 0)
          : (item.entity_profile_id ?? item.id ?? 0)
      );

      const entity_type_name =
        Reflect.get(item || {}, "Job Applied For") ??
        Reflect.get(item || {}, "Entity Type") ??
        item.entity_type_name ??
        item._child_raw?.entity_type_name ??
        "Entity";

      return {
        is_child,
        type_id,
        application_id,
        entity_profile_id,
        entity_type_name,
      };
    },

    async open_assign_modal(item) {
      const n = this.normalize_item(item);

      this.current_form.parent_id = n.type_id;
      this.current_form.parent_name = n.entity_type_name || "";
      this.current_form.parent_application_id = n.application_id || "";
      this.current_form.parent_entity_profile_id = n.entity_profile_id || "";

      this.assign_sub_entity_type_id = "";
      this.assign_form_list = [];
      this.assign_selected_form_id = "";
      this.assign_first_name = "";
      this.assign_last_name = "";
      this.assign_email = "";
      this.assign_phone_number = "";

      const assigned_rows = await this.get_assigned_children(this.current_form.parent_id);
      this.assign_sub_entity_options = (assigned_rows || []).map(r => ({
        id: Number(r.child_entity_type_id),
        name: r.child_entity_type_name || "Unnamed",
      }));

      $("#open_assign_modal_btn").trigger("click");
    },

    async assign_on_sub_entity_change() {
      this.assign_form_list = [];
      this.assign_selected_form_id = '';

      const entity_type_id = Number(this.assign_sub_entity_type_id || 0);
      if (!entity_type_id) return;

      try {
        const res = await axios.get(GET_ENTITY_TYPE_FORMS, {
          headers: this.headers,
          params: { entity_type_id }
        });

        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;

        this.assign_form_list = Array.isArray(data?.data) ? data.data : [];
      } catch (e) {
        this.show_error('Error getting forms for selected sub-entity: ' + (e.response?.data?.message || e.message));
      }
    },

    async get_assigned_children(parent_entity_type_id) {
      try {
        const res = await axios.get(
          LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
          {
            headers: this.headers,
            params: { parent_entity_type_id },
          }
        );

        if (res.data?.status !== 'success') {
          this.show_error('Could not load assigned children.');
          return [];
        }

        const payload = res.data?.data;
        this.displayed_assigned_children = payload;
        return Array.isArray(payload)
          ? payload
          : (payload?.assigned_children || []);
      } catch (error) {
        const msg = error?.response?.data?.message
          || error?.message
          || 'Failed to load assigned children.';
        this.show_error(msg);
        return [];
      }
    },

    async get_sub_entities(parent_application_id, entity_profile_id) {
      return this.show_loader("Loading sub-entities...")
        .then(loader => {
          try {
            const params = { parent_application_id: Number(parent_application_id) };
            if (Number.isInteger(entity_profile_id) && entity_profile_id > 0) {
              params.entity_profile_id = Number(entity_profile_id);
            }

            return axios.get(LIST_SUB_ENTITIES_BY_PARENT_APPLICATION, {
              headers: this.headers,
              params,
            })
              .then(res => {
                const body = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
                if (body?.status !== "success") {
                  this.show_error(body?.message || "could not load sub_entities");
                  return this.close_loader(loader).then(() => []);
                }

                const sub_entities = Array.isArray(body?.data) ? body.data : (body?.data?.children || []);
                return this.close_loader(loader).then(() => sub_entities || []);
              })
              .catch(error => {
                const msg = error?.response?.data?.message || error?.message || "failed to load sub_entities";
                this.show_error(msg);
                return this.close_loader(loader).then(() => []);
              });
          } catch (error) {
            const msg = error?.message || "failed to load sub_entities";
            this.show_error(msg);
            return this.close_loader(loader).then(() => []);
          }
        });
    },

    async fetch_assigned_for_item(item) {
      const parent_app_id = Number(
        Reflect.get(item || {}, "Application No") ?? item.application_id ?? item.id
      );
      if (!parent_app_id) {
        this.show_error("missing parent_application_id");
        return;
      }

      const entity_profile_id = item.is_child ? Number(item.id) : undefined;

      if (item.is_loading) return; // guard
      item.is_loading = true;

      try {
        const children = await this.get_sub_entities(parent_app_id, entity_profile_id);

        item.children = (children || []).map((child) =>
          this.make_node_from_child_entity(child, parent_app_id)
        );

        item._children_count = item.children.length;
        item._children_checked = true;
        item.has_children = item.children.length > 0;

        item._children_loaded = true;
        item.expanded = true;// auto-open only if there are kids
      } catch (err) {
        item._children_checked = true;
        item.has_children = false;
        item._children_loaded = true;
        item.expanded = false;
        item.is_loading = false;
      }
    },

    normalize_child_attributes(child) {
      if (Array.isArray(child?.attributes)) return child.attributes;

      const f0 = Array.isArray(child?.forms) ? child.forms[0] : null;
      if (f0?.attributes && Array.isArray(f0.attributes)) return f0.attributes;

      if (Array.isArray(f0?.answers)) {
        return f0.answers.map(a => ({
          attribute_id: a.question_id,
          attribute_name: a.question_text,
          attribute_type: a.type,
          value: a.value,
          has_value: a.value != null && String(a.value).trim() !== '',
          entity_type_attribute_id: a.entity_type_attribute_id ?? a.question_id,
        }));
      }

      return [];
    },

    make_node_from_child_entity(child, parent_app_id) {
      const application_id = Array.isArray(child.forms) && child.forms.length
        ? Number(child.forms[0].application_id)
        : Number(parent_app_id) || 0;

      const name = child.name || "Sub-entity not yet onboarded";
      const created_at = Array.isArray(child.forms) && child.forms[0]
        ? (child.forms[0].date_created || "")
        : "";

      const node = {
        id: Number(child.id),
        entity_profile_id: Number(child.id),
        application_id,
        entity_type_id: Number(child.entity_type_id),
        is_child: true,
        has_children: Boolean(child.has_children),
        children: [],
        expanded: false,
        Status: "",
        _child_raw: child,

        _children_checked: false,
        _children_loaded: false,
        is_loading: false,

        _attributes: this.normalize_child_attributes(child),
        "Application No": application_id,
        "Entity Name": name,
        "Entity Type": child.entity_type_name || "Entity",
        "Date Created": created_at,
      };

      node._show_all_attrs = false;

      return node;
    },

    async load_entity_type_context() {
      const project_id = parseInt(this.project_id, 10);

      if (!validate_non_sub_zero_number(project_id)) {
        this.show_error('Project Id must not be null');
        return;
      }

      try {
        const response = await axios.get(VIEW_ENTITY_TYPE_URL, {
          headers: this.headers,
          params: { project_id }
        });

        const response_data = response.data;
        if (response_data.status === 'success') {
          const entity_types = response_data.data || [];
          this.available_children = entity_types
            .filter(c => Number(c.id) !== Number(this.current_form.parent_id));
          this.entity_type = entity_types.map(entity => ({
            id: entity.id,
            name: entity.entity_type_name,
            description: entity.entity_type_description
          }));
        } else {
          this.show_error('Error entity types retrieval');
        }
      } catch (error) {
        this.show_error('Error entity types retrieval: ' + (error.response?.data?.message || error.message));
      }
    },

    validate_phone_number(phone_number) {
      return validate_phone_number(phone_number);
    },

    extract_applicant_response_list(payload) {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.results)) return payload.results;
      if (Array.isArray(payload?.adverts)) return payload.adverts;
      if (Array.isArray(payload?.job_adverts)) return payload.job_adverts;
      if (Array.isArray(payload?.data?.adverts)) return payload.data.adverts;
      if (Array.isArray(payload?.data?.job_adverts)) return payload.data.job_adverts;
      if (Array.isArray(payload?.data?.results)) return payload.data.results;
      return [];
    },

    async load_applicant_job_adverts(entity_type_id = "") {
      const project_id = parseInt(this.project_id, 10);
      const safe_entity_type_id = Number(entity_type_id || 0);
      const params = {
        ...(Number.isFinite(safe_entity_type_id) && safe_entity_type_id > 0 ? { entity_type_id: safe_entity_type_id } : {}),
        ...(!Number.isFinite(safe_entity_type_id) || safe_entity_type_id <= 0
          ? (validate_non_sub_zero_number(project_id) ? { project_id } : {})
          : {}),
      };

      try {
        const response = await axios.get(LIST_JOB_ADVERTS_URL, {
          headers: this.headers,
          params,
        });
        const adverts = this.extract_applicant_response_list(response?.data);
        this.job_adverts_list = adverts;
      } catch (_) {
        this.job_adverts_list = [];
      }
    },

    get_forms() {
      this.form_list = [];

      axios
        .get(GET_ENTITY_TYPE_FORMS, {
          headers: this.headers,
          params: { entity_type_id: this.entity_type_id },
        })
        .then((response) => {
          const parsed_response = JSON.parse(response.data);

          if (parsed_response.data && parsed_response.data.length > 0) {
            this.form_list = parsed_response.data;
          }
        })
        .catch((error) => {
          this.show_error("Error getting forms: " + error);
        });
    },

    create_application() {
      if (!this.entity_type_id) {
        this.show_error('Please select an entity type');
        return;
      }
      if (!this.selected_form_id) {
        this.show_error('Please select a form');
        return;
      }
      const entity_name = (this.entity_name || '').trim();
      if (!entity_name) {
        this.show_error('Please enter the entity name');
        return;
      }

      let email = null;
      if (this.include_email) {
        email = (this.assign_email || "").trim();
        if (!email || !validate_email(email)) {
          this.show_error("Please enter a valid email address.");
          return;
        }
      }

      const payload = {
        entity_type_id: Number(this.entity_type_id),
        form_ids: [Number(this.selected_form_id)],
        entity_name: entity_name,
        ...(this.project_id ? { project_id: Number(this.project_id) } : {}),
        ...(this.include_email && { email }),
      };

      const api_url = this.include_email ? ENTITY_REGISTER : CREATE_ENTITY_APPLICATION_ONLY;


      axios.post(api_url, payload, { headers: this.headers })
        .then((response) => {
          if (this.include_email && JSON.parse(response.data)) {
            this.show_success('Entity created and invitation email sent.');
            this.close_modal('create_applicant');
            this.entity_type_id = '';
            this.selected_form_id = '';
            this.entity_name = '';
          } else if (!this.include_email && response.data) {
            this.show_success('Entity and application created successfully.');
            this.close_modal('create_applicant');
            this.entity_type_id = '';
            this.selected_form_id = '';
            this.entity_name = '';
          }
        })
        .catch((error) => {
          this.show_error(error.response?.data?.message || 'Error creating entity/application');
          this.close_modal('create_applicant');
        });
    },

    // Add this method to your component
    get_application_by_id(app_id) {
      if (!Number.isSafeInteger(app_id) || app_id <= 0) {
        return null;
      }

      // Check if application exists in pending_applications_list first
      const from_pending_list = this.pending_applications_list.find(app => app.id === app_id);
      if (from_pending_list) {
        return from_pending_list;
      }

      // Fall back to application_row_map
      if (Reflect.has(this.application_row_map, app_id)) {
        const app = Object.getOwnPropertyDescriptor(this.application_row_map, app_id)?.value;
        return app;
      }

      return null;
    },

    get_applicant_card_original(row) {
      const application_id = Number.parseInt(Reflect.get(row || {}, "Application No"), 10);
      return Number.isFinite(application_id) ? this.get_application_by_id(application_id) : null;
    },

    get_applicant_row_key(row) {
      return Reflect.get(row || {}, "Application No") || this.get_applicant_reference(row);
    },

    get_applicant_card_name(row) {
      return Reflect.get(row || {}, "Full Name")
        || Reflect.get(row || {}, "Surname and Initials")
        || "Applicant";
    },

    get_applicant_initials(row) {
      return this.get_applicant_card_name(row)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "A";
    },

    get_applicant_reference(row) {
      const original = this.get_applicant_card_original(row);
      return original?.application_number
        || original?.reference_number
        || original?.application_ref
        || `APP-${String(Reflect.get(row || {}, "Application No") || "").padStart(4, "0")}`;
    },

    get_applicant_source_advert(row) {
      const original = this.get_applicant_card_original(row);
      return original?.advert_title
        || Reflect.get(row || {}, "Advert")
        || Reflect.get(row || {}, "Job Applied For")
        || this.entity_type_name
        || "Job Advert";
    },

    get_applicant_score_percent(row) {
      const original = this.get_applicant_card_original(row);
      const score = this.get_compliance_score(original);
      if (score !== null) return Math.round(score);
      const score_result = this.get_orcaa_score_result(original || {});
      const fallback = score_result?.overall_score ?? score_result?.score ?? score_result?.percentage;
      const parsed = this.parse_percent_value(fallback);
      return parsed !== null ? Math.round(parsed) : null;
    },

    get_applicant_score_width(row) {
      const score = this.get_applicant_score_percent(row);
      return `${score === null ? 0 : Math.max(0, Math.min(100, score))}%`;
    },

    get_applicant_score_class(row) {
      const score = this.get_applicant_score_percent(row);
      if (score === null || score >= 80) return "applicant-score-good";
      if (score >= 50) return "applicant-score-warn";
      return "applicant-score-bad";
    },

    get_applicant_badge_label(row) {
      const original = this.get_applicant_card_original(row);
      return this.get_threshold_assessment(original || {}) || "Pending";
    },

    get_applicant_badge_class(row) {
      const normalized = this.normalize_threshold_assessment(this.get_applicant_badge_label(row));
      if (normalized === "qualified") return "applicant-pill-qualified";
      if (normalized === "partially_qualified") return "applicant-pill-partial";
      if (normalized === "unqualified") return "applicant-pill-unqualified";
      return "applicant-pill-pending";
    },

    get_applicant_stage_label(row) {
      const original = this.get_applicant_card_original(row);
      const status = String(this.get_application_status_name(original) || Reflect.get(row || {}, "Status") || "").toLowerCase();
      if (status.includes("shortlisted")) return "Shortlisted";
      return "Pending";
    },

    get_applicant_stage_class(row) {
      return this.get_applicant_stage_label(row) === "Shortlisted"
        ? "applicant-pill-shortlisted"
        : "applicant-pill-pending";
    },

    get_applicant_requirement_checks(row) {
      const fields = [
        "New Z83 attached, duly completed, initialed and signed (Y/N)",
        "CV attached (Y/N)",
        this.get_required_qualification_header(),
        this.get_required_experience_header(),
      ];
      return fields.map((field) => {
        const value = String(Reflect.get(row || {}, field) || "")
          .split(/\r?\n/, 1)[0]
          .trim()
          .toLowerCase();
        const passed = ["y", "yes", "true", "1", "pass", "passed"].includes(value);
        const failed = ["n", "no", "false", "0", "fail", "failed"].includes(value);
        return { passed, failed };
      });
    },

    clear_applicant_filters() {
      this.filters = {
        ...this.filters,
        search: "",
        entity_type_id: "",
        advert_id: "",
      };
      this.active_compliance_card = "all";
      this.apply_application_filters();
    },

    async on_applicant_profile_filter_changed() {
      this.filters.advert_id = "";
      await this.load_applicant_job_adverts(this.filters.entity_type_id);
      this.apply_application_filters();
    },

    on_filters_changed(new_filters) {
      this.filters = { ...this.filters, ...new_filters };
      this.apply_application_filters();
    },

    is_master_registered(row) {
      const original = this.get_application_by_id(Reflect.get(row || {}, "Application No"));
      if (!original) return false;

      const raw_status = this.get_application_status_name(original);
      return raw_status.toLowerCase().includes('master registered') ||
        raw_status.toLowerCase().includes('master_registered');
    },

    handle_check_children(item) {
      this.check_if_has_children(item);
    },

    check_all_items_for_children() {
      if (this.displayed_applications_list && this.displayed_applications_list.length > 0) {
        this.displayed_applications_list.forEach(item => {
          if (!item._children_checked && !item.is_child) {
            this.check_if_has_children(item);
          }
        });
      }
    },

    async check_if_has_children(item) {
      const parent_app_id = Number(
        Reflect.get(item || {}, "Application No") ?? item.application_id ?? item.id
      );
      if (!parent_app_id) {
        item.has_children = false;
        return;
      }

      const entity_profile_id = item.is_child ? Number(item.id) : undefined;

      try {
        const children = await this.get_sub_entities(parent_app_id, entity_profile_id);

        item.has_children = children && children.length > 0;
        item._children_checked = true; // Mark as checked

      } catch (err) {
        item.has_children = false;
        item._children_checked = true;
      }
    },

    async save_assign_sub_entity() {
      return this.show_loader("Saving assigned sub-entity...")
        .then(loader => {
          if (!this.current_form?.parent_application_id) {
            this.show_error("Missing parent application.");
            return this.close_loader(loader);
          }

          const child_type_id = Number(this.assign_sub_entity_type_id || 0);
          if (!child_type_id) {
            this.show_error("Please select a sub-entity type.");
            return this.close_loader(loader);
          }

          const selected_form_id = Number(this.assign_selected_form_id || 0);
          if (!selected_form_id) {
            this.show_error("Please select a form for the sub-entity.");
            return this.close_loader(loader);
          }

          const entity_name = (this.assign_entity_name || "").trim();
          if (!entity_name) {
            this.show_error("Please enter the sub-entity name.");
            return this.close_loader(loader);
          }

          let email = null;
          if (this.include_email) {
            email = (this.assign_email || "").trim();
            if (!email || !validate_email(email)) {
              this.show_error("Please enter a valid email address.");
              return this.close_loader(loader);
            }
          }

          const payload = {
            parent_application_id: Number(this.current_form.parent_application_id),
            entity_type_id: child_type_id,
            parent_entity_type_id: Number(this.current_form.parent_id),
            parent_entity_profile_id: Number(this.current_form.parent_entity_profile_id),
            project_id: Number(this.project_id),
            form_ids: [selected_form_id],
            entity_name: entity_name,
            ...(this.include_email && { email }),
          };

          const api_url = this.include_email ? ENTITY_REGISTER : CREATE_ENTITY_APPLICATION_ONLY;

          return axios.post(api_url, payload, { headers: this.headers })
            .then(resp => {
              var response_data;
              if (this.include_email) {
                response_data = JSON.parse(resp.data);
              } else {
                response_data = resp.data;
              }

              if (response_data?.status !== "success") {
                this.show_error(response_data?.message || "Failed to assign sub-entity.");
                return this.close_loader(loader);
              }

              this.show_success("Sub-entity added and form created.");

              this.assign_sub_entity_type_id = "";
              this.assign_form_list = [];
              this.assign_selected_form_id = "";
              this.assign_entity_name = "";
              this.assign_email = "";

              const parent_row = this.displayed_applications_list.find(
                r => Number(r.id) === Number(this.current_form.parent_id)
              );
              if (parent_row) {
                return this.fetch_assigned_for_item(parent_row)
                  .then(() => this.close_loader(loader));
              } else {
                return this.close_loader(loader);
              }
            })
            .catch(e => {
              const msg = e?.response?.data?.message || e.message || "Error assigning the sub-entity.";
              this.show_error(msg);
              return this.close_loader(loader);
            });
        });
    },
  },
};
